import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { orderingQuantities } from '../lib/orderingQuantities'
import { createOrderingSync } from '../lib/orderingSync'
import { Link } from 'react-router-dom'
import {
  RefreshCw,
  Printer,
  Download,
  Upload,
  Package,
  Droplets,
  Leaf,
  Snowflake,
  Beef,
  Wheat,
  ChefHat,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Settings2,
  GripVertical,
  Check,
  X,
  ExternalLink,
  FileSpreadsheet,
  AlertCircle,
  Search,
  Link2,
  StickyNote,
  Plus,
  Trash2,
  Split,
  Boxes,
  Utensils,
  Scale,
  BookOpen,
  Save,
  AlertTriangle,
  Edit2,
  Undo2,
  Redo2,
  History,
  Clock,
  CheckCircle2,
  Circle,
  UtensilsCrossed
} from 'lucide-react'
import { useApp } from '../App'
import { orderingAPI, inventoryAPI, exportAPI, menusAPI, recipesAPI, dishesAPI, suppliersAPI, formatQuantity, SUPPLIERS, DEFAULT_COLUMNS, DISPLAY_UNITS, convertFromLbs, convertToLbs, getUnitLabel } from '../lib/api'
import { tr, trIngredient } from '../lib/translations'
import { useToastActions } from '../components/Toast'
import { scaleTarget } from '../lib/batch'
import { usedOnRows } from '../lib/orderingUsedOn'
import { FileCode } from 'lucide-react'
import { buildHavnContext, mdTable, downloadMD, buildFilename, fmtNum } from '../lib/mdExport'

// ══════════════════════════════════════════════════════════════════════════════
// MARKDOWN EXPORT HELPERS — LLM-optimized order & prep sheets
// ══════════════════════════════════════════════════════════════════════════════

function _itemDishes(item) {
  return (item.dishes || [])
    .map((d) => d.dish_name || d.name || d)
    .filter(Boolean)
    .join('; ')
}

function _renderItemsTable(items, opts = {}) {
  if (!items?.length) return '_No items in this category._\n\n'
  const rows = items.map((it) => {
    const name = it.ingredient_name || it.display_name || ''
    const dishes = _itemDishes(it)
    return [
      name,
      fmtNum(it.needed_with_buffer ?? it.needed_raw ?? 0),
      fmtNum(it.on_hand ?? 0),
      fmtNum(it.to_order ?? 0),
      fmtNum(it.amount_ordered ?? 0),
      it.ordered ? 'yes' : 'no',
      it.is_on_hand ? 'yes' : 'no',
      it.supplier_name || it.supplier || '',
      dishes,
      it.notes || '',
    ]
  })
  return (
    mdTable(
      ['Ingredient', 'Needed (lbs)', 'On Hand', 'To Order', 'Ordered Amt', 'Ordered?', 'Plenty?', 'Supplier', 'Used In', 'Notes'],
      rows,
    ) + '\n\n'
  )
}

function buildOrderSheetMD(data, menuName, cityId) {
  let md = buildHavnContext({
    docType: 'Order Sheet',
    docPurpose: 'Ingredient purchasing sheet — what to order, in what quantity, from which supplier, based on this week\'s meal counts. Includes a 5% buffer over raw needs.',
    city: cityId,
    menu: menuName,
  })

  md += `# Order Sheet${menuName ? ` — ${menuName}` : ''}\n\n`

  // Summary
  const summary = data.summary || {}
  md += `## Summary\n\n`
  md += `- **Total meals:** ${summary.total_meals ?? '—'}\n`
  md += `- **Ingredients:** ${summary.total_ingredients ?? '—'}\n`
  md += `- **Sauces tracked:** ${summary.sauces_count ?? (data.sauces?.length ?? '—')}\n`
  md += `- **Additional ingredients (recipes-only):** ${summary.additional_count ?? '—'}\n\n`

  md += `> Columns: **Needed** = lbs with 5% buffer. **On Hand** = lbs already in stock. **To Order** = Needed − On Hand (zero if "Plenty?" is yes). **Plenty?** = staff marked "we have enough, skip ordering". **Ordered Amt** = lbs actually ordered so far.\n\n`

  const categories = [
    { key: 'proteins', label: 'Proteins' },
    { key: 'vegetables', label: 'Produce' },
    { key: 'carbs', label: 'Carbs & Grains' },
    { key: 'sauces', label: 'Sauces' },
    { key: 'bases', label: 'Prepared sides & toppings' },
  ]

  categories.forEach(({ key, label }) => {
    const items = data[key] || []
    if (!items.length) return
    md += `## ${label} (${items.length})\n\n`
    md += _renderItemsTable(items)
  })

  if (data.additional) {
    md += `## Additional (recipe-driven)\n\n`
    md += `> These ingredients come from recipe ingredient lists (not from dish components). Common pantry items: liquids, dry goods, refrigerated items.\n\n`
    const addCats = [
      { key: 'liquids', label: 'Liquids' },
      { key: 'dry_goods', label: 'Dry Goods' },
      { key: 'refrigerated', label: 'Refrigerated' },
      { key: 'proteins', label: 'Additional Proteins' },
    ]
    addCats.forEach(({ key, label }) => {
      const items = data.additional[key] || []
      if (!items.length) return
      md += `### ${label} (${items.length})\n\n`
      md += _renderItemsTable(items)
    })
  }

  return md
}

function buildPrepSheetMD(data, menuName, cityId) {
  let md = buildHavnContext({
    docType: 'Prep Sheet',
    docPurpose: 'Kitchen prep list — per-component lbs to prep this week, with which dishes each component appears in. Used to plan batch cooking.',
    city: cityId,
    menu: menuName,
  })

  md += `# Prep Sheet${menuName ? ` — ${menuName}` : ''}\n\n`
  md += `> "Needed" includes a 5% buffer over raw demand. Sort prep order: sauces & bases first (need cooling), then proteins, then vegetables (freshness).\n\n`

  const categories = [
    { key: 'proteins', label: 'Proteins' },
    { key: 'vegetables', label: 'Produce' },
    { key: 'carbs', label: 'Carbs & Grains' },
    { key: 'sauces', label: 'Sauces' },
    { key: 'bases', label: 'Prepared sides & toppings' },
    { key: 'dry_goods', label: 'Dry goods toppings' },
    { key: 'refrigerated', label: 'Refrigerated toppings' },
  ]

  categories.forEach(({ key, label }) => {
    const items = (data[key] || data.additional?.[key] || []).filter((i) => i.components?.length && (i.needed_with_buffer ?? i.needed_raw ?? 0) > 0)
    if (!items.length) return
    md += `## ${label} (${items.length})\n\n`
    md += mdTable(
      ['Component', 'Needed (lbs)', 'Used In'],
      items
        .slice()
        .sort((a, b) => (b.needed_with_buffer ?? 0) - (a.needed_with_buffer ?? 0))
        .map((it) => [
          it.ingredient_name || it.display_name || '',
          fmtNum(it.needed_with_buffer ?? it.needed_raw ?? 0),
          _itemDishes(it),
        ]),
    )
    md += `\n\n`
  })

  return md
}

// ══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════════════════════

const CATEGORY_CONFIG = {
  proteins: { icon: Beef, color: 'bg-red-600', label: 'Proteins' },
  vegetables: { icon: Leaf, color: 'bg-green-600', label: 'Produce' },
  carbs: { icon: Wheat, color: 'bg-amber-600', label: 'Carbs & Grains' },
  sauces: { icon: Droplets, color: 'bg-purple-600', label: 'Sauces' },
  bases: { icon: ChefHat, color: 'bg-blue-600', label: 'Prepared sides & toppings' },
  wellness_shots: { icon: Droplets, color: 'bg-emerald-500', label: 'Wellness Shots' },
  liquids: { icon: Droplets, color: 'bg-cyan-600', label: 'Liquids' },
  dry_goods: { icon: Package, color: 'bg-orange-600', label: 'Dry Goods' },
  refrigerated: { icon: Snowflake, color: 'bg-sky-600', label: 'Refrigerated' },
}

// ══════════════════════════════════════════════════════════════════════════════
// NOTE: All calculations are now done by the backend!
// The frontend is DISPLAY ONLY - it shows what the backend returns.
// When a user edits a value (on_hand, lbs_per_meal, ordered), we:
// 1. Save to backend
// 2. Refetch to get the new calculated values
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get color for ordered amount based on deviation from needed amount
 */
function getOrderedStatusColor(amountOrdered, neededAmount) {
  if (!amountOrdered || amountOrdered === 0) return null
  if (!neededAmount || neededAmount === 0) return 'bg-emerald-100 text-emerald-700'

  const deviation = Math.abs(amountOrdered - neededAmount) / neededAmount

  if (deviation <= 0.05) return 'bg-emerald-100 text-emerald-800' // Within 5% - GREEN
  if (deviation <= 0.10) return 'bg-amber-100 text-amber-800'     // Within 5-10% - YELLOW
  return 'bg-red-100 text-red-800'                                // More than 10% off - RED
}

// ══════════════════════════════════════════════════════════════════════════════
// UNDO/REDO HOOK
// ══════════════════════════════════════════════════════════════════════════════

function useUndoRedo(maxHistory = 50) {
  const [history, setHistory] = useState([])
  const [currentIndex, setCurrentIndex] = useState(-1)
  const [lastSaved, setLastSaved] = useState(null)

  const historyRef = useRef(history)
  const currentIndexRef = useRef(currentIndex)

  useEffect(() => {
    historyRef.current = history
  }, [history])

  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])

  const canUndo = currentIndex > 0
  const canRedo = currentIndex < history.length - 1

  const pushState = useCallback((state, description = 'Change') => {
    const idx = currentIndexRef.current
    setHistory(prev => {
      const newHistory = prev.slice(0, idx + 1)
      newHistory.push({ state: JSON.parse(JSON.stringify(state)), description, timestamp: Date.now() })
      if (newHistory.length > maxHistory) {
        newHistory.shift()
        return newHistory
      }
      return newHistory
    })
    setCurrentIndex(prev => Math.min(prev + 1, maxHistory - 1))
  }, [maxHistory])

  const undo = useCallback(() => {
    const idx = currentIndexRef.current
    const hist = historyRef.current
    if (idx > 0) {
      setCurrentIndex(idx - 1)
      return hist[idx - 1]?.state
    }
    return null
  }, [])

  const redo = useCallback(() => {
    const idx = currentIndexRef.current
    const hist = historyRef.current
    if (idx < hist.length - 1) {
      setCurrentIndex(idx + 1)
      return hist[idx + 1]?.state
    }
    return null
  }, [])

  const getUndoDescription = useCallback(() => {
    const idx = currentIndexRef.current
    return idx > 0 ? historyRef.current[idx - 1]?.description : ''
  }, [])

  const getRedoDescription = useCallback(() => {
    const idx = currentIndexRef.current
    const hist = historyRef.current
    return idx < hist.length - 1 ? hist[idx + 1]?.description : ''
  }, [])

  return {
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    history,
    currentIndex,
    getUndoDescription,
    getRedoDescription,
    lastSaved,
    setLastSaved
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY
// ══════════════════════════════════════════════════════════════════════════════

const VERSION_STORAGE_KEY = 'havn_ordering_versions'
const AUTO_SAVE_INTERVAL = 30 * 1000  // Auto-save to database every 30 seconds
const LOCAL_VERSION_INTERVAL = 2 * 60 * 1000  // Local version history every 2 minutes
const MAX_VERSIONS = 5

// Clean up localStorage keys for old menus (keeps only current menu + 1 previous)
function pruneOldVersionKeys(currentMenuId) {
  if (!currentMenuId) return
  const prefix = `${VERSION_STORAGE_KEY}_`
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.startsWith(prefix) && key !== `${prefix}${currentMenuId}`) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k))
}

// Generate a unique browser identifier for tracking who made changes
const getBrowserId = () => {
  let browserId = localStorage.getItem('havn_browser_id')
  if (!browserId) {
    browserId = `browser_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    localStorage.setItem('havn_browser_id', browserId)
  }
  return browserId
}

function useVersionHistory(menuId) {
  const [versions, setVersions] = useState([])
  const [showVersionModal, setShowVersionModal] = useState(false)

  useEffect(() => {
    if (menuId) {
      pruneOldVersionKeys(menuId)
      const stored = localStorage.getItem(`${VERSION_STORAGE_KEY}_${menuId}`)
      if (stored) {
        try {
          setVersions(JSON.parse(stored))
        } catch (e) {
          console.error('Failed to parse stored versions', e)
        }
      }
    }
  }, [menuId])

  const saveVersion = useCallback((data, description = 'Auto-save') => {
    if (!menuId || !data) return

    const newVersion = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      description,
      data: JSON.parse(JSON.stringify(data))
    }

    const tryStore = (key, versions) => {
      try {
        localStorage.setItem(key, JSON.stringify(versions))
        return versions
      } catch (e) {
        return null
      }
    }

    setVersions(prev => {
      const key = `${VERSION_STORAGE_KEY}_${menuId}`
      const updated = [newVersion, ...prev].slice(0, MAX_VERSIONS)
      // Try storing all versions, then fewer, then just the newest
      return tryStore(key, updated)
        ?? tryStore(key, updated.slice(0, 2))
        ?? tryStore(key, [newVersion])
        ?? [newVersion] // localStorage full — keep in memory only
    })
  }, [menuId])

  const revertToVersion = useCallback((versionId) => {
    const version = versions.find(v => v.id === versionId)
    return version?.data || null
  }, [versions])

  const clearVersions = useCallback(() => {
    setVersions([])
    if (menuId) {
      localStorage.removeItem(`${VERSION_STORAGE_KEY}_${menuId}`)
    }
  }, [menuId])

  return {
    versions,
    saveVersion,
    revertToVersion,
    clearVersions,
    showVersionModal,
    setShowVersionModal
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// VERSION HISTORY MODAL
// ══════════════════════════════════════════════════════════════════════════════

function VersionHistoryModal({ versions, onRevert, onClose }) {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-md">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-havn-green-100 flex items-center justify-center">
              <History className="w-5 h-5 text-havn-green-700" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                Version History
              </h3>
              <p className="text-xs text-havn-gray-500">
                Auto-saved every 2 minutes
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {versions.length === 0 ? (
            <div className="text-center py-8 text-havn-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No saved versions yet</p>
              <p className="text-xs mt-1">Versions are auto-saved every 2 minutes</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className="flex items-center justify-between p-3 bg-havn-cream-50 rounded-lg hover:bg-havn-cream-100 transition-colors"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-havn-charcoal">
                        {formatTime(version.timestamp)}
                      </span>
                      {index === 0 && (
                        <span className="px-1.5 py-0.5 text-xs bg-havn-green-100 text-havn-green-700 rounded">
                          Latest
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-havn-gray-500 mt-0.5">{version.description}</p>
                  </div>
                  <button
                    onClick={() => onRevert(version.id)}
                    className="px-3 py-1.5 text-xs font-medium text-havn-green-700 hover:bg-havn-green-100 rounded-lg transition-colors"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// RECOVERY MODAL (Database Snapshots)
// ══════════════════════════════════════════════════════════════════════════════

function RecoveryModal({ snapshots, loading, onRestore, onClose, onRefresh }) {
  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case 'auto': return 'Auto-save'
      case 'bulk_save': return 'Full Save'
      default: return type
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'auto': return 'bg-blue-100 text-blue-700'
      case 'bulk_save': return 'bg-green-100 text-green-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-lg">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                Recover Data
              </h3>
              <p className="text-xs text-havn-gray-500">
                Restore from automatic database backups
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <div className="flex gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Data Recovery</p>
                <p className="text-xs text-amber-700 mt-1">
                  Select a snapshot below to restore your ordering data. This will overwrite current values with the saved snapshot.
                </p>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 text-havn-green-600 animate-spin" />
              <p className="text-havn-gray-500">Loading recovery points...</p>
            </div>
          ) : snapshots.length === 0 ? (
            <div className="text-center py-8 text-havn-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No snapshots available</p>
              <p className="text-xs mt-1">Snapshots are saved automatically every 30 seconds</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {snapshots.map((snapshot, index) => (
                <div
                  key={snapshot.id}
                  className="flex items-center justify-between p-3 bg-havn-cream-50 rounded-lg hover:bg-havn-cream-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-havn-charcoal">
                        {formatTime(snapshot.created_at)}
                      </span>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${getTypeColor(snapshot.type)}`}>
                        {getTypeLabel(snapshot.type)}
                      </span>
                      {index === 0 && (
                        <span className="px-1.5 py-0.5 text-xs bg-havn-green-100 text-havn-green-700 rounded">
                          Most Recent
                        </span>
                      )}
                    </div>
                    {snapshot.user && (
                      <p className="text-xs text-havn-gray-500 mt-0.5">
                        Saved by: {snapshot.user.substring(0, 20)}...
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => onRestore(snapshot.id)}
                    className="px-3 py-1.5 text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg transition-colors"
                  >
                    Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer flex justify-between">
          <button onClick={onRefresh} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// INLINE EDITABLE CELL
// ══════════════════════════════════════════════════════════════════════════════

function InlineEditCell({ value, onChange, type = 'number', suffix = '', className = '', step = 1, decimals = 1, disabled = false }) {
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value)  // Track local state for display
  const [tempValue, setTempValue] = useState(value)
  const inputRef = useRef(null)
  const editActive = useRef(false)

  // Update local value when prop changes (e.g., after a page refresh)
  useEffect(() => {
    setLocalValue(value)
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select()
    }
  }, [editing])

  function handleSave() {
    if (!editActive.current) return
    editActive.current = false
    const newValue = type === 'number' ? parseFloat(tempValue) || 0 : tempValue
    setLocalValue(newValue)  // Update local state immediately
    onChange(newValue)
    setEditing(false)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') {
      editActive.current = false
      setTempValue(localValue)
      setEditing(false)
    }
  }

  // Check if className already has a background color (status indicator)
  const hasStatusBg = className.includes('bg-')

  // Use localValue for display (persists across edits without refresh)
  const displayValue = localValue

  if (disabled) {
    return (
      <div className={`w-full px-2 py-1 text-sm text-havn-gray-400 ${className}`}>
        {type === 'number' ? formatQuantity(displayValue, decimals) : displayValue}{suffix}
      </div>
    )
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`w-full px-2 py-1 text-sm border border-havn-green-600 rounded focus:outline-none focus:ring-1 focus:ring-havn-green-600 ${className}`}
        step={type === 'number' ? step : undefined}
      />
    )
  }

  return (
    <button
      onClick={() => { editActive.current = true; setTempValue(displayValue); setEditing(true) }}
      className={`w-full text-left px-2 py-1 rounded transition-colors cursor-text ${hasStatusBg ? '' : 'hover:bg-havn-cream-200'} ${className}`}
    >
      {type === 'number' ? formatQuantity(displayValue, decimals) : displayValue}{suffix}
    </button>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ON-HAND EDIT CELL
// ══════════════════════════════════════════════════════════════════════════════

function OnHandEditCell({ value, onChange, unit = 'lbs', isOnHand = false, onIsOnHandChange, disabled = false }) {
  const [localValue, setLocalValue] = useState(value || 0)
  const [editing, setEditing] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    setLocalValue(value || 0)
  }, [value])

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.select()
    }
  }, [editing])

  function handleSave() {
    const newValue = parseFloat(localValue) || 0
    if (newValue !== value) {
      onChange(newValue)
    }
    setEditing(false)
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSave()
    if (e.key === 'Escape') {
      setLocalValue(value || 0)
      setEditing(false)
    }
  }

  const displayValue = localValue % 1 === 0 ? Math.floor(localValue) : localValue.toFixed(1)

  return (
    <div className="flex items-center justify-center gap-1">
      {/* On Hand Checkbox Icon */}
      <button
        onClick={() => onIsOnHandChange && onIsOnHandChange(!isOnHand)}
        className={`p-1 rounded transition-colors ${
          isOnHand
            ? 'text-emerald-600 hover:text-emerald-700 bg-emerald-50'
            : 'text-havn-gray-300 hover:text-emerald-500 hover:bg-emerald-50'
        }`}
        title={isOnHand ? 'Click to unmark as "on hand"' : 'Click to mark as "we have plenty"'}
      >
        {isOnHand ? (
          <CheckCircle2 className="w-4 h-4" />
        ) : (
          <Circle className="w-4 h-4" />
        )}
      </button>

      {/* Value Input */}
      {editing ? (
        <input
          ref={inputRef}
          type="number"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="w-16 px-2 py-1 text-sm text-center border border-havn-green-600 rounded focus:outline-none focus:ring-1 focus:ring-havn-green-600"
          step="1"
          disabled={disabled}
        />
      ) : (
        <button
          onClick={() => !disabled && setEditing(true)}
          className={`w-16 px-2 py-1 text-sm text-center rounded transition-colors ${
            disabled ? 'text-havn-gray-400 cursor-default' : 'hover:bg-havn-cream-200 cursor-text'
          }`}
          disabled={disabled}
        >
          {isOnHand ? '✓' : `${displayValue} ${unit}`}
        </button>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// USED IN CELL (Compact but clickable)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Displays the "Used In" column - shows which components/recipes use this ingredient.
 * Recipe names are clickable to open the scaled recipe view.
 * Click anywhere in cell to expand when there are more items.
 *
 * For main ingredients:
 * - If component has a recipe (like "Roasted Broccoli"), show the recipe name
 * - If component has NO recipe (like "Chicken Thigh"), show the DISH name (like "Tuscan Chicken")
 * For additional ingredients:
 * - Show source recipe names
 */
function UsedInCell({ components, sourceRecipes, dishes, isExpanded, onToggleExpand, onViewRecipe }) {
  const { isSpanish, translations } = useApp()
  // Build the items to show:
  // - For additional ingredients: use source_recipes
  // - For main ingredients: use components, but show dish name for items without recipes
  let itemsToShow = []

  if (sourceRecipes && sourceRecipes.length > 0) {
    // Additional ingredients - show recipe names
    itemsToShow = sourceRecipes.map(recipe => ({
      name: recipe.name,
      recipe_id: recipe.recipe_id,
      recipe_json_id: recipe.recipe_json_id,
      needed_lbs: recipe.quantity_lbs,
      _kind: 'recipe',
      _translate_id: recipe.recipe_id,
    }))
  } else if (dishes && dishes.length > 0) {
    // Main ingredients — show the DISH/MEAL name (e.g., "Chicken Tikka")
    // With per-dish rows, each row has one component linked to one dish.
    // The dish name tells culinary staff which meal this component belongs to.
    // We still carry the recipe_id from the component so clicking opens the recipe.
    const compMap = {}
    if (components && components.length > 0) {
      components.forEach((comp, idx) => {
        // Map by index for pairing with dishes
        compMap[idx] = comp
      })
    }

    itemsToShow = dishes.map((dish, idx) => {
      const comp = compMap[idx] || (components && components[0]) || {}
      return {
        name: dish.dish_name,
        recipe_id: comp.recipe_id || null,
        recipe_json_id: comp.recipe_json_id || null,
        needed_lbs: comp.needed_lbs || 0,
        _kind: 'dish',
        _translate_id: dish.dish_id,
      }
    })

    // Deduplicate by name
    const seen = new Set()
    itemsToShow = itemsToShow.filter(item => {
      if (seen.has(item.name)) return false
      seen.add(item.name)
      return true
    })
  } else if (components && components.length > 0) {
    // Fallback: if no dishes data, show component names
    itemsToShow = components.map(comp => ({
      name: comp.name,
      recipe_id: comp.recipe_id,
      recipe_json_id: comp.recipe_json_id,
      needed_lbs: comp.needed_lbs,
      _kind: 'recipe',
      _translate_id: comp.recipe_id,
    }))
  }

  if (!itemsToShow || itemsToShow.length === 0) {
    return <span className="text-sm text-havn-gray-400">-</span>
  }

  const count = itemsToShow.length
  // Show first 2 items max when collapsed
  const MAX_VISIBLE = 2
  const hasMore = count > MAX_VISIBLE
  const visibleItems = itemsToShow.slice(0, MAX_VISIBLE)

  const handleRecipeClick = (item, e) => {
    e.stopPropagation()
    if (!onViewRecipe) return
    const recipeId = item.recipe_json_id || item.recipe_id
    const targetQty = item.needed_lbs || item.quantity_lbs || 10
    if (recipeId) {
      onViewRecipe(recipeId, item.name, targetQty, item.count)
    }
  }

  // Click anywhere in cell to expand (if there are more items)
  const handleCellClick = (e) => {
    if (hasMore && !isExpanded) {
      onToggleExpand()
    }
  }

  return (
    <div
      onClick={handleCellClick}
      className={`flex items-center gap-1 text-sm flex-wrap ${hasMore && !isExpanded ? 'cursor-pointer' : ''}`}
    >
      {visibleItems.map((item, idx) => {
        const rawName = item.name || item
        const translatedName = tr(
          isSpanish,
          translations,
          item._kind === 'dish' ? 'dishes' : 'recipes',
          item._translate_id,
          'name',
          rawName,
        )
        const hasRecipe = item.recipe_json_id || item.recipe_id

        return (
          <span key={idx}>
            {hasRecipe ? (
              <button
                onClick={(e) => handleRecipeClick(item, e)}
                className="text-purple-600 hover:text-purple-800 hover:underline"
                title={`View ${rawName} recipe (scaled)`}
              >
                {translatedName}
              </button>
            ) : (
              <span className="text-havn-gray-600">{translatedName}</span>
            )}
            {idx < visibleItems.length - 1 && <span className="text-havn-gray-400">, </span>}
          </span>
        )
      })}
      {hasMore && !isExpanded && (
        <span className="text-havn-gray-400 ml-0.5">
          +{count - MAX_VISIBLE}
        </span>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MEAL CELL (shows which dishes/meals use this ingredient)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Displays the "Meal" column - shows which dishes (meals) use this ingredient.
 * Click on a dish name to view the full dish details.
 */
function MealCell({ dishes, onViewDish }) {
  const { isSpanish, translations } = useApp()
  if (!dishes || dishes.length === 0) {
    return <span className="text-sm text-havn-gray-400">-</span>
  }

  const MAX_VISIBLE = 2
  const hasMore = dishes.length > MAX_VISIBLE
  const visibleDishes = dishes.slice(0, MAX_VISIBLE)

  return (
    <div className="flex items-center gap-1 text-sm">
      {visibleDishes.map((dish, idx) => {
        const displayName = tr(isSpanish, translations, 'dishes', dish.dish_id, 'name', dish.dish_name)
        return (
          <button
            key={dish.dish_id}
            onClick={(e) => { e.stopPropagation(); onViewDish && onViewDish(dish) }}
            className="text-havn-gray-700 hover:text-havn-green-700 hover:underline"
            title={`View ${dish.dish_name}`}
          >
            {displayName}{idx < visibleDishes.length - 1 ? ',' : ''}
          </button>
        )
      })}
      {hasMore && (
        <span className="text-havn-gray-400">+{dishes.length - MAX_VISIBLE}</span>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPANDED USED-IN ROW
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Expansion row showing all components/recipes this ingredient is used in.
 * Rendered below the main ingredient row when expanded.
 */
function UsedInExpandedRow({ components, sourceRecipes, colSpan, onViewRecipe }) {
  const itemsToShow = (components && components.length > 0) ? components : (sourceRecipes || [])

  const handleClick = (item) => {
    if (!onViewRecipe) return
    const recipeId = item.recipe_json_id || item.recipe_id
    const targetQty = item.needed_lbs || item.quantity_lbs || 10
    if (recipeId) {
      onViewRecipe(recipeId, item.name, targetQty, item.count)
    }
  }

  return (
    <tr className="bg-purple-50/50">
      <td colSpan={colSpan} className="px-4 py-3">
        <div className="flex flex-wrap gap-2">
          {itemsToShow.map((item, idx) => {
            const itemName = item.name || item
            const hasRecipe = item.recipe_json_id || item.recipe_id
            const mealCount = item.count || 0
            const neededLbs = item.needed_lbs || item.quantity_lbs || 0

            return (
              <button
                key={idx}
                onClick={() => handleClick(item)}
                disabled={!hasRecipe}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  hasRecipe
                    ? 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-100 hover:border-purple-300 cursor-pointer shadow-sm'
                    : 'bg-white border border-havn-cream-200 text-havn-gray-600 cursor-default'
                }`}
                title={hasRecipe ? `View ${itemName} recipe (scaled)` : itemName}
              >
                <span className="font-medium">{itemName}</span>
                {mealCount > 0 && (
                  <span className="text-xs text-havn-gray-500">
                    {mealCount} meals
                  </span>
                )}
                {neededLbs > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                    {formatQuantity(neededLbs)} lbs
                  </span>
                )}
                {hasRecipe && <BookOpen className="w-3.5 h-3.5 text-purple-500" />}
              </button>
            )
          })}
        </div>
      </td>
    </tr>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SUPPLIER DROPDOWN
// ══════════════════════════════════════════════════════════════════════════════

function SupplierDropdown({ value, onChange, productUrl, suppliers = SUPPLIERS }) {
  const [open, setOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, openUpward: false })
  const buttonRef = useRef(null)
  const hasUrl = productUrl && productUrl.trim()

  // Get supplier names from array of objects or strings
  const supplierNames = suppliers.map(s => typeof s === 'string' ? s : s.name)

  const handleOpen = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpward = spaceBelow < 200
      setDropdownPos({
        top: openUpward ? rect.top + window.scrollY : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        openUpward,
      })
    }
    setOpen(!open)
  }

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors
          ${hasUrl ? 'text-havn-green-700 hover:bg-havn-green-50 underline decoration-dotted' : 'hover:bg-havn-cream-200'}`}
      >
        {value || '-'}
        <ChevronDown className="w-3 h-3" />
        {hasUrl && <ExternalLink className="w-3 h-3" />}
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed bg-white rounded-lg shadow-lg border border-havn-cream-300 z-[9999] min-w-[120px]"
            style={{
              top: dropdownPos.openUpward ? undefined : `${dropdownPos.top}px`,
              bottom: dropdownPos.openUpward ? `${window.innerHeight - dropdownPos.top + 4}px` : undefined,
              left: `${dropdownPos.left}px`,
            }}
          >
            {supplierNames.map(supplier => (
              <button
                key={supplier}
                onClick={() => {
                  setOpen(false)
                  setTimeout(() => onChange(supplier), 0)
                }}
                className={`w-full px-3 py-2 text-sm text-left hover:bg-havn-cream-100 transition-colors
                  ${supplier === value ? 'bg-havn-cream-50 text-havn-green-700 font-medium' : ''}`}
              >
                {supplier}
              </button>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// INGREDIENT DETAIL MODAL
// ══════════════════════════════════════════════════════════════════════════════

function UsedOnTable({ ingredient }) {
  const rows = usedOnRows(ingredient)
  if (!rows.length) return null
  return (
    <div>
      <h4 className="text-sm font-medium text-havn-charcoal mb-2 flex items-center gap-2">
        <UtensilsCrossed className="w-4 h-4" />
        Ends up on {rows.length} dish component{rows.length !== 1 ? 's' : ''}
      </h4>
      <div className="bg-havn-cream-50 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-havn-cream-100">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-havn-gray-600">Component</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-havn-gray-600">Dish</th>
              <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">Needed</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-havn-cream-200">
            {rows.map((row) => (
              <tr key={row.key}>
                <td className="px-3 py-2 text-havn-charcoal">
                  {row.component}
                  {row.via && <span className="block text-xs text-havn-gray-500">via {row.via}</span>}
                </td>
                <td className="px-3 py-2 text-havn-gray-600">{row.dish}</td>
                <td className="px-3 py-2 text-right font-medium text-havn-green-700">{row.lbs != null ? `${formatQuantity(row.lbs)} lbs` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function IngredientDetailModal({ ingredient, cityId, menuId, onClose, onUpdate, onViewRecipe }) {
  const [productUrl, setProductUrl] = useState(ingredient.product_url || '')
  const [notes, setNotes] = useState(ingredient.notes || '')
  const [displayUnit, setDisplayUnit] = useState(ingredient.display_unit || 'lbs')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const { success, error } = useToastActions()

  // Track if there are unsaved changes
  const hasChanges = productUrl !== (ingredient.product_url || '') ||
                     notes !== (ingredient.notes || '') ||
                     displayUnit !== (ingredient.display_unit || 'lbs')

  async function handleSave() {
    try {
      setSaving(true)
      // Save supplier/product URL/notes
      await orderingAPI.updateSupplier(
        ingredient.ingredient_id,
        cityId,
        ingredient.supplier_name,
        productUrl,
        notes
      )
      // Save display unit if changed
      if (displayUnit !== (ingredient.display_unit || 'lbs')) {
        await orderingAPI.updateDisplayUnit(menuId, ingredient.ingredient_id, displayUnit)
      }
      success('Saved')
      setSaved(true)
      // Don't call onUpdate() here - avoid refreshing the whole page
      // Don't auto-close - let user close when ready
    } catch (err) {
      error('Failed to update ingredient')
    } finally {
      setSaving(false)
    }
  }

  // When closing, refresh if we made changes
  function handleClose() {
    if (saved) {
      onUpdate() // Refresh data only when closing after a save
    }
    onClose()
  }

  // Get components/recipes breakdown (for main ingredients)
  const componentBreakdown = useMemo(() => {
    if (!ingredient.components || ingredient.components.length === 0) return []

    return ingredient.components.map(comp => {
      const compName = typeof comp === 'string' ? comp : comp.name
      const mealCount = comp.count || 0
      const lbsPerMeal = ingredient.lbs_per_meal || 0.2
      const contribution = comp.needed_lbs || mealCount * lbsPerMeal

      return {
        name: compName,
        recipe_id: comp.recipe_json_id || comp.recipe_id,
        count: mealCount,
        lbsPerMeal,
        contribution,
      }
    })
  }, [ingredient.components, ingredient.lbs_per_meal])

  // Get recipe breakdown for additional ingredients
  const recipeBreakdown = useMemo(() => {
    if (!ingredient.source_recipes || ingredient.source_recipes.length === 0) return []

    return ingredient.source_recipes.map(recipe => ({
      recipe_id: recipe.recipe_json_id || recipe.recipe_id,
      name: recipe.name,
      originalQuantity: recipe.quantity || 0,
      originalUnit: recipe.unit || 'lbs',
      quantityInLbs: recipe.quantity_lbs || recipe.quantity,
    }))
  }, [ingredient.source_recipes])

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && handleClose()}>
      <div className="modal max-w-2xl">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-havn-green-100 flex items-center justify-center">
              <Scale className="w-5 h-5 text-havn-green-700" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                {ingredient.ingredient_name}
              </h3>
              <p className="text-xs text-havn-gray-500">
                {ingredient.is_additional ? 'Additional Ingredient (from recipes)' : ingredient.component_type || 'Ingredient'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body space-y-5">
          {/* Unit Selector */}
          <div className="flex items-center gap-3 p-3 bg-havn-cream-50 rounded-lg">
            <Scale className="w-4 h-4 text-havn-gray-500" />
            <span className="text-sm text-havn-gray-600">Display units:</span>
            <select
              value={displayUnit}
              onChange={(e) => setDisplayUnit(e.target.value)}
              className="px-3 py-1.5 text-sm border border-havn-cream-300 rounded-lg focus:border-havn-green-600 focus:ring-1 focus:ring-havn-green-600 bg-white"
            >
              {Object.entries(DISPLAY_UNITS).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            {displayUnit !== (ingredient.display_unit || 'lbs') && (
              <span className="text-xs text-amber-600">• unsaved</span>
            )}
          </div>

          {ingredient.purchase_items?.length > 0 && (
            <div className="p-3 rounded-lg border border-havn-cream-300 text-sm">
              <p className="font-medium text-havn-charcoal">One purchase line</p>
              <p className="text-havn-gray-600 mt-1">
                The recipe buys this as {ingredient.purchase_items.map((item) => `${item.name} (${formatQuantity(item.quantity_lbs)} lbs in ${item.recipe_name})`).join(', ')}.
                Component amount {formatQuantity(ingredient.component_raw_lbs)} lbs; recipe amount {formatQuantity(ingredient.recipe_raw_lbs)} lbs.
                Ordering the larger ({ingredient.quantity_source === 'recipe' ? 'recipe' : 'component'}) amount.
              </p>
            </div>
          )}

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-havn-cream-100 rounded-lg">
              <span className="text-xs text-havn-gray-500 uppercase tracking-wide">Needed</span>
              <p className="text-lg font-semibold text-havn-charcoal">
                {formatQuantity(convertFromLbs(ingredient.needed_with_buffer, displayUnit))} {getUnitLabel(displayUnit)}
              </p>
            </div>
            <div className="p-3 bg-havn-cream-100 rounded-lg">
              <span className="text-xs text-havn-gray-500 uppercase tracking-wide">On Hand</span>
              <p className="text-lg font-semibold text-havn-charcoal">
                {formatQuantity(convertFromLbs(ingredient.on_hand, displayUnit))} {getUnitLabel(displayUnit)}
              </p>
            </div>
            <div className="p-3 bg-havn-green-50 rounded-lg">
              <span className="text-xs text-havn-gray-500 uppercase tracking-wide">To Order</span>
              <p className="text-lg font-semibold text-havn-green-700">
                {formatQuantity(convertFromLbs(ingredient.to_order, displayUnit))} {getUnitLabel(displayUnit)}
              </p>
            </div>
          </div>

          <UsedOnTable ingredient={ingredient} />

          {/* Breakdown by Component/Recipe (for main ingredients) */}
          {!ingredient.is_additional && componentBreakdown.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-havn-charcoal mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Used in {componentBreakdown.length} component{componentBreakdown.length !== 1 ? 's' : ''}
              </h4>
              <div className="bg-havn-cream-50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-havn-cream-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-havn-gray-600">Component</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">Meals</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">x lbs/meal</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">= Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-havn-cream-200">
                    {componentBreakdown.map((comp, i) => (
                      <tr
                        key={i}
                        className={comp.recipe_id ? 'cursor-pointer hover:bg-havn-cream-100' : ''}
                        onClick={() => comp.recipe_id && onViewRecipe && onViewRecipe(comp.recipe_id, comp.name, comp.contribution)}
                      >
                        <td className="px-3 py-2 text-havn-charcoal">
                          <span className={comp.recipe_id ? 'text-purple-600 hover:underline' : ''}>
                            {comp.name}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-havn-gray-600">{comp.count}</td>
                        <td className="px-3 py-2 text-right text-havn-gray-600">
                          {formatQuantity(comp.lbsPerMeal, 2)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-havn-green-700">
                          {formatQuantity(comp.contribution)} lbs
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Breakdown by Recipe (for additional ingredients) */}
          {ingredient.is_additional && recipeBreakdown.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-havn-charcoal mb-2 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Used in {recipeBreakdown.length} recipe{recipeBreakdown.length !== 1 ? 's' : ''}
              </h4>
              <div className="bg-havn-cream-50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-havn-cream-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-havn-gray-600">Recipe</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">Original</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-havn-gray-600">Scaled</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-havn-cream-200">
                    {recipeBreakdown.map((recipe, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-havn-charcoal">{recipe.name}</td>
                        <td className="px-3 py-2 text-right text-havn-gray-500">
                          {formatQuantity(recipe.originalQuantity)} {recipe.originalUnit}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-havn-green-700">
                          {formatQuantity(recipe.quantityInLbs)} lbs
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Product URL */}
          <div>
            <label className="block text-sm font-medium text-havn-charcoal mb-1">
              <Link2 className="w-4 h-4 inline mr-1" />
              Product Link
            </label>
            <input
              type="url"
              value={productUrl}
              onChange={(e) => setProductUrl(e.target.value)}
              placeholder="https://supplier.com/product/123"
              className="input"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-havn-charcoal mb-1">
              <StickyNote className="w-4 h-4 inline mr-1" />
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this ingredient..."
              className="input min-h-[80px]"
            />
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={handleClose} className="btn-secondary">
            {saved ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || saved || !hasChanges}
            className={`btn-primary ${saved ? 'bg-green-600 hover:bg-green-600' : ''}`}
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <Check className="w-4 h-4" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            {saved ? 'Saved' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SCALED RECIPE MODAL
// ══════════════════════════════════════════════════════════════════════════════

function ScaledRecipeModal({ recipeId, dishId, recipeName, targetQuantity, targetCount, isDish, onClose }) {
  const [recipe, setRecipe] = useState(null)
  const [scaledRecipe, setScaledRecipe] = useState(null)
  const [dish, setDish] = useState(null)
  const [loading, setLoading] = useState(true)
  const [targetQty, setTargetQty] = useState(targetQuantity || 10)
  const { error } = useToastActions()

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)

        if (isDish && dishId) {
          const dishData = await dishesAPI.get(dishId)
          setDish(dishData)
          setScaledRecipe({
            recipe_name: dishData.name,
            category: dishData.category,
            ingredients: dishData.components?.map(c => ({
              ingredient_name: c.name,
              quantity: c.portion_oz,
              unit: 'oz',
              component_type: c.component_type,
              order_factor: c.order_factor_lbs,
              order_factor_unit: c.component_type === 'protein' && /\bchicken\b/i.test(`${c.name} ${c.raw_ingredient_name || ''}`) ? 'lb / 6 oz portion' : 'lbs/meal',
            })) || [],
            chef_notes: dishData.description || ''
          })
          setLoading(false)
          return
        }

        if (recipeId) {
          // Load the recipe before scaling: a "makes 50 units" batch must be
          // scaled by PORTIONS, and we can't know which it is until we've read
          // base_batch_unit. Scaling such a recipe against the pound figure is
          // what made 33 portions render as a quarter batch (33 x 0.375 lbs).
          const originalRecipe = await recipesAPI.get(recipeId)
          setRecipe(originalRecipe)
          const resolved = scaleTarget(originalRecipe?.base_batch_unit,
                                       targetCount, targetQty) || targetQty
          if (resolved !== targetQty) setTargetQty(resolved)
          setScaledRecipe(await recipesAPI.scale(recipeId, resolved))
        }
      } catch (err) {
        error('Failed to load data')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [recipeId, dishId, targetQty, isDish])

  async function handleScaleChange(newQty) {
    try {
      const scaled = await recipesAPI.scale(recipeId, newQty)
      setScaledRecipe(scaled)
      setTargetQty(newQty)
    } catch (err) {
      error('Failed to scale recipe')
    }
  }

  if (loading) {
    return (
      <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-havn-green-700 animate-spin" />
          </div>
        </div>
      </div>
    )
  }

  // Show error state if recipe couldn't be loaded
  if (!isDish && !scaledRecipe && !recipe) {
    return (
      <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal max-w-md">
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-display text-xl font-semibold text-havn-green-900">
                  {recipeName || 'Recipe Not Found'}
                </h3>
                <p className="text-xs text-havn-gray-500">
                  Could not load recipe details
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="modal-body">
            <p className="text-sm text-havn-gray-600">
              The recipe "{recipeName}" couldn't be loaded. This may happen if the recipe hasn't been set up in the recipes database yet.
            </p>
            <p className="text-xs text-havn-gray-400 mt-2">
              Recipe ID: {recipeId || 'None'}
            </p>
          </div>
          <div className="modal-footer">
            <button onClick={onClose} className="btn-secondary">Close</button>
          </div>
        </div>
      </div>
    )
  }

  const componentTypeConfig = {
    protein: { label: 'Protein', color: 'bg-red-100 text-red-700', icon: Beef },
    vegetable: { label: 'Vegetable', color: 'bg-green-100 text-green-700', icon: Leaf },
    carb: { label: 'Carb', color: 'bg-amber-100 text-amber-700', icon: Wheat },
    sauce: { label: 'Sauce', color: 'bg-purple-100 text-purple-700', icon: Droplets },
    base: { label: 'Base', color: 'bg-blue-100 text-blue-700', icon: ChefHat },
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-havn-brass-100 flex items-center justify-center">
              {isDish ? <Utensils className="w-5 h-5 text-havn-brass-600" /> : <BookOpen className="w-5 h-5 text-havn-brass-600" />}
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                {recipeName || scaledRecipe?.recipe_name || dish?.name}
              </h3>
              <p className="text-xs text-havn-gray-500">
                {isDish ? (
                  <>{dish?.category} - {targetQuantity} meals this week</>
                ) : (
                  <>{scaledRecipe?.category} - Base: {recipe?.base_batch_size} {recipe?.base_batch_unit}</>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body overflow-y-auto flex-1 space-y-5">
          {/* Scale Controls */}
          {isDish ? (
            <div className="flex items-center justify-between p-3 bg-havn-cream-100 rounded-lg">
              <div className="flex items-center gap-3">
                <Scale className="w-5 h-5 text-havn-green-700" />
                <span className="text-sm font-medium text-havn-charcoal">Meal Count:</span>
                <span className="text-lg font-semibold text-havn-green-700">{targetQuantity}</span>
                <span className="text-sm text-havn-gray-500">meals</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-havn-cream-100 rounded-lg">
              <div className="flex items-center gap-3">
                <Scale className="w-5 h-5 text-havn-green-700" />
                <span className="text-sm font-medium text-havn-charcoal">Scale to:</span>
                <input
                  type="number"
                  value={targetQty}
                  onChange={(e) => handleScaleChange(parseFloat(e.target.value) || 1)}
                  className="w-20 px-2 py-1 text-center font-semibold border border-havn-cream-300 rounded-lg focus:border-havn-green-600 focus:ring-1 focus:ring-havn-green-600"
                  step="0.5"
                  min="0.5"
                />
                <span className="text-sm text-havn-gray-500">{recipe?.base_batch_unit || 'lbs'}</span>
              </div>
              {scaledRecipe?.scale_factor && (
                <span className="text-xs text-havn-gray-400">
                  {scaledRecipe.scale_factor}x base recipe
                </span>
              )}
            </div>
          )}

          {/* Components/Ingredients */}
          <div>
            <h4 className="font-medium text-havn-charcoal mb-3">
              {isDish ? 'Components' : 'Ingredients'}
            </h4>
            <div className="space-y-2">
              {isDish ? (
                scaledRecipe?.ingredients?.map((comp, i) => {
                  const config = componentTypeConfig[comp.component_type] || { label: comp.component_type, color: 'bg-gray-100 text-gray-700', icon: Package }
                  const IconComponent = config.icon
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-havn-cream-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.color}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-havn-charcoal">{comp.ingredient_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-havn-gray-500">
                          <span>{formatQuantity(comp.quantity)} {comp.unit} per serving</span>
                          {comp.order_factor && (
                            <span>- Order: {formatQuantity(comp.order_factor)} {comp.order_factor_unit || 'lbs/meal'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                scaledRecipe?.ingredients?.map((ing, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 bg-havn-cream-50 rounded-lg">
                    <span className="flex-1 text-sm text-havn-charcoal">{ing.ingredient_name}</span>
                    <span className="text-sm font-medium text-havn-green-700 tabular-nums">
                      {formatQuantity(ing.quantity)} {ing.unit}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// COLUMN SETTINGS MODAL
// ══════════════════════════════════════════════════════════════════════════════

function ColumnSettingsModal({ columns, onChange, onClose }) {
  const [localColumns, setLocalColumns] = useState([...columns])

  function toggleColumn(id) {
    setLocalColumns(prev => prev.map(col =>
      col.id === id && !col.locked ? { ...col, visible: !col.visible } : col
    ))
  }

  function moveColumn(index, direction) {
    const newColumns = [...localColumns]
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= newColumns.length) return
    ;[newColumns[index], newColumns[newIndex]] = [newColumns[newIndex], newColumns[index]]
    setLocalColumns(newColumns)
  }

  function handleSave() {
    onChange(localColumns)
    onClose()
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-md">
        <div className="modal-header">
          <h3 className="font-display text-lg font-semibold text-havn-green-900">
            <Settings2 className="w-5 h-5 inline mr-2" />
            Column Settings
          </h3>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          <p className="text-sm text-havn-gray-500 mb-4">
            Show/hide and reorder columns.
          </p>
          <div className="space-y-1">
            {localColumns.map((col, i) => (
              <div
                key={col.id}
                className="flex items-center gap-2 p-2 bg-havn-cream-50 rounded-lg"
              >
                <GripVertical className="w-4 h-4 text-havn-gray-300 cursor-move" />
                <label className="flex items-center gap-2 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => toggleColumn(col.id)}
                    disabled={col.locked}
                    className="w-4 h-4 rounded border-havn-gray-300 text-havn-green-600 focus:ring-havn-green-500"
                  />
                  <span className={col.locked ? 'text-havn-gray-400' : 'text-havn-charcoal'}>
                    {col.label}
                  </span>
                </label>
                <div className="flex gap-1">
                  <button
                    onClick={() => moveColumn(i, -1)}
                    disabled={i === 0}
                    className="p-1 text-havn-gray-400 hover:text-havn-charcoal disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveColumn(i, 1)}
                    disabled={i === localColumns.length - 1}
                    className="p-1 text-havn-gray-400 hover:text-havn-charcoal disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSave} className="btn-primary">Save Layout</button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// INVENTORY TABLE (shows all imported CSV items at the bottom of the page)
// ══════════════════════════════════════════════════════════════════════════════

function InventoryTable({ items, menuId, onRefresh, onDataChange, forceExpanded }) {
  const [expanded, setExpanded] = useState(true)
  const [editingId, setEditingId] = useState(null)
  const [editValue, setEditValue] = useState('')
  const editInputRef = useRef(null)
  const { success, error } = useToastActions()

  useEffect(() => {
    if (forceExpanded !== undefined) setExpanded(forceExpanded)
  }, [forceExpanded])

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.select()
    }
  }, [editingId])

  if (!items || items.length === 0) return null

  const matchedCount = items.filter(i => i.matched_ingredient_id && !i.is_stale).length
  const staleCount = items.filter(i => i.is_stale).length
  const unmatchedCount = items.filter(i => !i.matched_ingredient_id && !i.is_stale).length

  async function handleQuantityEdit(item, newValue) {
    const qty = parseFloat(newValue) || 0
    if (qty === item.quantity) {
      setEditingId(null)
      return
    }
    try {
      await inventoryAPI.updateItem(menuId, item.id, {
        quantity: qty,
        is_on_hand: false
      })
      setEditingId(null)
      onRefresh()
      onDataChange?.()
    } catch (err) {
      error('Failed to update: ' + err.message)
    }
  }

  async function handleOnHandToggle(item) {
    const newIsOnHand = !item.is_on_hand
    try {
      await inventoryAPI.updateItem(menuId, item.id, {
        is_on_hand: newIsOnHand,
        quantity: newIsOnHand ? 0 : item.quantity
      })
      onRefresh()
      onDataChange?.()
    } catch (err) {
      error('Failed to update: ' + err.message)
    }
  }

  function handleKeyDown(e, item) {
    if (e.key === 'Enter') handleQuantityEdit(item, editValue)
    if (e.key === 'Escape') setEditingId(null)
  }

  function getConfidenceBadge(confidence) {
    if (!confidence) return null
    const colors = {
      high: 'bg-emerald-100 text-emerald-700',
      medium: 'bg-amber-100 text-amber-700',
      low: 'bg-red-100 text-red-700',
      auto: 'bg-blue-100 text-blue-700',
    }
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${colors[confidence] || 'bg-gray-100 text-gray-600'}`}>
        {confidence === 'auto' ? 'auto-matched' : confidence}
      </span>
    )
  }

  return (
    <div className="card mb-4">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-havn-cream-50 transition-colors rounded-t-xl"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
            <Boxes className="w-4 h-4 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-display text-sm font-semibold text-havn-green-900">
              Inventory
            </h3>
            <p className="text-xs text-havn-gray-400">
              {items.length} items imported
              {matchedCount > 0 && ` · ${matchedCount} matched`}
              {staleCount > 0 && ` · ${staleCount} stale`}
              {unmatchedCount > 0 && ` · ${unmatchedCount} unmatched`}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-havn-gray-400" /> : <ChevronDown className="w-4 h-4 text-havn-gray-400" />}
      </button>

      {/* Table */}
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t border-havn-cream-200">
                <th className="text-left text-xs font-medium text-havn-gray-500 uppercase tracking-wide px-3 py-2">Ingredient</th>
                <th className="text-center text-xs font-medium text-havn-gray-500 uppercase tracking-wide px-3 py-2 w-28">Amount</th>
                <th className="text-center text-xs font-medium text-havn-gray-500 uppercase tracking-wide px-3 py-2 w-20">Unit</th>
                <th className="text-center text-xs font-medium text-havn-gray-500 uppercase tracking-wide px-3 py-2 w-16">On Hand</th>
                <th className="text-left text-xs font-medium text-havn-gray-500 uppercase tracking-wide px-3 py-2">Matched To</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const isMatched = !!item.matched_ingredient_id && !item.is_stale
                const isStale = !!item.is_stale
                const isOnHand = !!item.is_on_hand
                const isEditing = editingId === item.id

                return (
                  <tr
                    key={item.id}
                    className={`border-t border-havn-cream-100 transition-colors ${
                      isStale ? 'bg-amber-50/60' :
                      isOnHand ? 'bg-emerald-50/50 text-havn-gray-400' :
                      !isMatched ? 'bg-amber-50/30' : 'hover:bg-havn-cream-50'
                    }`}
                  >
                    {/* Ingredient name */}
                    <td className="px-3 py-2">
                      <span className={`${!isMatched ? 'italic text-havn-gray-400' : 'text-havn-charcoal'} ${isOnHand ? 'line-through' : ''}`}>
                        {item.original_name}
                      </span>
                    </td>

                    {/* Amount (editable) */}
                    <td className="px-3 py-2 text-center">
                      {isOnHand ? (
                        <span className="text-emerald-600 font-medium text-xs">In Stock</span>
                      ) : isEditing ? (
                        <input
                          ref={editInputRef}
                          type="number"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => handleQuantityEdit(item, editValue)}
                          onKeyDown={(e) => handleKeyDown(e, item)}
                          className="w-20 px-2 py-1 text-sm text-center border border-havn-green-600 rounded focus:outline-none focus:ring-1 focus:ring-havn-green-600"
                          step="0.1"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditingId(item.id)
                            setEditValue(item.quantity || 0)
                          }}
                          className="w-20 px-2 py-1 text-sm text-center rounded hover:bg-havn-cream-200 cursor-text transition-colors"
                        >
                          {item.quantity % 1 === 0 ? Math.floor(item.quantity) : Number(item.quantity).toFixed(1)}
                        </button>
                      )}
                    </td>

                    {/* Unit */}
                    <td className="px-3 py-2 text-center text-xs text-havn-gray-500">
                      {isOnHand ? '' : (item.unit || item.original_unit || '')}
                    </td>

                    {/* On Hand toggle */}
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => handleOnHandToggle(item)}
                        className={`p-1 rounded transition-colors ${
                          isOnHand
                            ? 'text-emerald-600 hover:text-emerald-700 bg-emerald-50'
                            : 'text-havn-gray-300 hover:text-emerald-500 hover:bg-emerald-50'
                        }`}
                        title={isOnHand ? 'Unmark as on hand' : 'Mark as on hand (we have plenty)'}
                      >
                        {isOnHand ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                      </button>
                    </td>

                    {/* Matched To */}
                    <td className="px-3 py-2">
                      {isStale ? (
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                          <span className="text-xs text-amber-600 font-medium">No longer on menu</span>
                          <span className="text-[10px] text-amber-500 italic">({item.matched_to})</span>
                        </div>
                      ) : isMatched ? (
                        <div className="flex items-center gap-1.5">
                          <Link2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                          <span className="text-xs text-havn-charcoal">{item.matched_to}</span>
                          {getConfidenceBadge(item.confidence)}
                        </div>
                      ) : (
                        <span className="text-xs text-havn-gray-400 italic">Not on menu</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


// ══════════════════════════════════════════════════════════════════════════════
// IMPORT MODAL
// ══════════════════════════════════════════════════════════════════════════════

function ImportModal({ menuId, cityId, onClose, onSuccess }) {
  const [file, setFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const { success, error } = useToastActions()
  const fileInputRef = useRef(null)

  async function handleImport() {
    if (!file) return

    try {
      setImporting(true)
      const data = await inventoryAPI.import(menuId, cityId, file)
      setResult(data)
      if (data.success) {
        success(`Imported ${data.total_items} items (${data.items_matched} matched)`)
        onSuccess()
      }
    } catch (err) {
      error('Import failed: ' + err.message)
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-lg">
        <div className="modal-header">
          <h3 className="font-display text-lg font-semibold text-havn-green-900">
            <FileSpreadsheet className="w-5 h-5 inline mr-2" />
            Import Inventory
          </h3>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body">
          {!result ? (
            <>
              <p className="text-sm text-havn-gray-500 mb-4">
                Upload a CSV or Excel file with your inventory. Expected columns: <strong>Ingredient</strong>, <strong>Amount</strong>, <strong>Units</strong>.
                If an amount says "on hand", the item will be marked as in stock.
              </p>

              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-havn-cream-300 rounded-xl p-8 text-center cursor-pointer
                         hover:border-havn-green-400 hover:bg-havn-cream-50 transition-colors"
              >
                <Upload className="w-10 h-10 text-havn-gray-300 mx-auto mb-3" />
                <p className="text-havn-charcoal font-medium">
                  {file ? file.name : 'Click to upload or drag & drop'}
                </p>
                <p className="text-xs text-havn-gray-400 mt-1">
                  CSV or Excel files supported
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="hidden"
              />
            </>
          ) : (
            <div className="animate-fade-in">
              {result.success ? (
                <>
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 mx-auto mb-3 flex items-center justify-center">
                      <Check className="w-6 h-6" />
                    </div>
                    <h4 className="font-semibold text-havn-charcoal">Import Complete!</h4>
                  </div>

                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-emerald-700">{result.items_matched}</p>
                      <p className="text-xs text-emerald-600">Matched</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-blue-700">{result.items_on_hand}</p>
                      <p className="text-xs text-blue-600">On Hand</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-amber-700">{result.items_unmatched}</p>
                      <p className="text-xs text-amber-600">Unmatched</p>
                    </div>
                  </div>

                  <p className="text-xs text-havn-gray-400 text-center">
                    {result.total_items} total items imported. See the Inventory section below for details.
                  </p>

                  {/* Warnings */}
                  {result.warnings && result.warnings.length > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs font-medium text-amber-700 mb-1">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        Warnings
                      </p>
                      {result.warnings.map((w, i) => (
                        <p key={i} className="text-xs text-amber-600">{w}</p>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h4 className="font-semibold text-havn-charcoal">Import Failed</h4>
                  <p className="text-sm text-havn-gray-500 mt-1">{result.error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={handleImport} disabled={!file || importing} className="btn-primary">
              {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {importing ? 'Analyzing...' : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SPLIT ORDER ROW
// ══════════════════════════════════════════════════════════════════════════════

function SplitOrderRow({ order, suppliers, onUpdate, onRemove, isLast }) {
  const [useCases, setUseCases] = useState(order.cases > 0 || order.case_size > 0)
  const total = useCases ? (order.cases || 0) * (order.case_size || 0) : (order.amount || 0)

  return (
    <div className={`py-2 ${!isLast ? 'border-b border-havn-cream-200' : ''}`}>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 text-xs text-havn-gray-400 w-20">
          <Split className="w-3 h-3" />
          <span>Order #{order.index + 1}</span>
        </div>
        <select
          value={order.supplier || ''}
          onChange={(e) => onUpdate({ ...order, supplier: e.target.value })}
          className="px-2 py-1 text-xs border border-havn-cream-300 rounded focus:border-havn-green-500 w-28"
        >
          <option value="">Supplier...</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button
          onClick={() => {
            setUseCases(!useCases)
            if (useCases) onUpdate({ ...order, cases: 0, case_size: 0 })
            else onUpdate({ ...order, amount: 0 })
          }}
          className={`px-2 py-1 text-xs rounded transition-colors ${useCases ? 'bg-purple-100 text-purple-700' : 'bg-havn-cream-200 text-havn-gray-600'}`}
        >
          <Boxes className="w-3 h-3 inline mr-1" />{useCases ? 'Cases' : 'Amount'}
        </button>
        {useCases ? (
          <div className="flex items-center gap-1 flex-1">
            <input type="number" value={order.cases || ''} onChange={(e) => onUpdate({ ...order, cases: parseInt(e.target.value) || 0 })} placeholder="# cases" className="w-16 px-2 py-1 text-xs border border-havn-cream-300 rounded focus:border-havn-green-500" />
            <span className="text-xs text-havn-gray-400">x</span>
            <input type="number" value={order.case_size || ''} onChange={(e) => onUpdate({ ...order, case_size: parseFloat(e.target.value) || 0 })} placeholder="lbs/case" className="w-20 px-2 py-1 text-xs border border-havn-cream-300 rounded focus:border-havn-green-500" />
            <span className="text-xs text-havn-gray-500 font-medium">= {formatQuantity(total)} lbs</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 flex-1">
            <input type="number" value={order.amount || ''} onChange={(e) => onUpdate({ ...order, amount: parseFloat(e.target.value) || 0 })} placeholder="Amount" className="w-20 px-2 py-1 text-xs border border-havn-cream-300 rounded focus:border-havn-green-500" />
            <span className="text-xs text-havn-gray-500">lbs</span>
          </div>
        )}
        <button onClick={onRemove} className="p-1 text-havn-gray-400 hover:text-red-500 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SAUCE ROW - Display only (made in-house)
// ══════════════════════════════════════════════════════════════════════════════

function SauceRow({ item, onViewRecipe, menuId, cityId, onDataChange, onItemPatch }) {
  // Get recipe ID for clicking
  const recipeId = item.recipe_json_id || item.recipe_id
  const hasRecipe = !!recipeId
  const { error } = useToastActions()
  const isShared = item.is_shared && item.dishes?.length > 1
  const kitchenName = item.display_name || item.ingredient_name
  const purchaseName = item.ingredient_name !== kitchenName ? item.ingredient_name : null

  // Local state for editable lbs/meal (used for non-shared sauces)
  const [localLbsPerMeal, setLocalLbsPerMeal] = useState(item.lbs_per_meal || 0)
  const [saving, setSaving] = useState(false)

  // For shared sauces, track per-dish lbs/meal locally
  const [dishLbsPerMeal, setDishLbsPerMeal] = useState(() => {
    if (!isShared) return {}
    const map = {}
    item.dishes?.forEach(d => {
      map[d.dish_id] = d.lbs_per_meal || item.lbs_per_meal || 0
    })
    return map
  })

  // Sync local state when item changes
  useEffect(() => {
    setLocalLbsPerMeal(item.lbs_per_meal || 0)
    if (isShared) {
      const map = {}
      item.dishes?.forEach(d => {
        map[d.dish_id] = d.lbs_per_meal || item.lbs_per_meal || 0
      })
      setDishLbsPerMeal(map)
    }
  }, [item.ingredient_id, item.lbs_per_meal])

  // Compute needed locally for instant feedback
  // For shared sauces, sum per-dish computations
  const computedDishes = isShared
    ? item.dishes.map(d => ({
        ...d,
        localLbsPerMeal: dishLbsPerMeal[d.dish_id] || d.lbs_per_meal || 0,
        localNeeded: (d.meal_count || 0) * (dishLbsPerMeal[d.dish_id] || d.lbs_per_meal || 0),
        localNeededWithBuffer: (d.meal_count || 0) * (dishLbsPerMeal[d.dish_id] || d.lbs_per_meal || 0) * 1.05,
      }))
    : []

  const localNeededRaw = item.is_dependency || item.has_dependency ? item.needed_raw : isShared
    ? computedDishes.reduce((sum, d) => sum + d.localNeeded, 0)
    : (item.total_meal_count || 0) * localLbsPerMeal
  const localNeededWithBuffer = localNeededRaw * 1.05

  const handleSauceClick = () => {
    if (hasRecipe && onViewRecipe) {
      onViewRecipe(recipeId, item.display_name || item.ingredient_name,
                   item.needed_with_buffer || 10, item.total_meal_count)
    }
  }

  // Save lbs/meal to backend on blur
  async function handleLbsPerMealChange(newValue) {
    setLocalLbsPerMeal(newValue)
    if (!item.ingredient_id) return
    try {
      setSaving(true)
      await orderingAPI.updateOrderFactor(item.ingredient_id, newValue, cityId, menuId)
      onItemPatch?.(item.ingredient_id, { lbs_per_meal: newValue })
      onDataChange?.()
    } catch (err) {
      error('Failed to update')
      setLocalLbsPerMeal(item.lbs_per_meal || 0)
    } finally {
      setSaving(false)
    }
  }

  // ── Non-shared sauce: original simple row ──
  if (!isShared || item.is_dependency || item.has_dependency) {
    const mealNames = [...(item.dishes?.map(d => d.dish_name || d.name || d).filter(Boolean) || []), ...(item.used_in_recipes || [])]
    return (
      <tr className="bg-purple-50/30 hover:bg-purple-50/50 transition-colors border-b border-havn-cream-200">
        <td className="px-3 py-3 text-sm">
          {hasRecipe ? (
            <button onClick={handleSauceClick}
              className="font-medium text-base text-purple-700 hover:text-purple-900 hover:underline"
              title={`View ${kitchenName} recipe`}
            >{kitchenName}</button>
          ) : (
            <div className="font-medium text-base text-purple-700">{kitchenName}</div>
          )}
          {purchaseName && <div className="text-xs text-havn-gray-500 mt-0.5">Purchase as: {purchaseName}</div>}
        </td>
        <td className="px-3 py-3 text-center">
          <div className="text-sm text-havn-gray-600">
            {mealNames.slice(0, 2).join(', ')}
            {mealNames.length > 2 && <span className="text-havn-gray-400"> +{mealNames.length - 2}</span>}
          </div>
        </td>
        <td className="px-3 py-3 text-center tabular-nums text-base text-havn-gray-600">
          {item.total_meal_count || 0}
        </td>
        <td className="px-3 py-3 text-center">
          {item.is_dependency || item.has_dependency ? <span className="text-xs text-purple-700">From recipes</span> : <input type="number" value={localLbsPerMeal}
            onChange={(e) => setLocalLbsPerMeal(parseFloat(e.target.value) || 0)}
            onBlur={(e) => { const val = parseFloat(e.target.value) || 0; if (val !== (item.lbs_per_meal || 0)) handleLbsPerMealChange(val) }}
            onFocus={(e) => e.target.select()} step="0.05" min="0"
            className={`w-20 text-center text-sm tabular-nums rounded-lg border px-2 py-1.5 transition-colors
              ${saving ? 'border-yellow-400 bg-yellow-50' : 'border-havn-cream-300 bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'}`}
          />}
        </td>
        <td className="px-3 py-3 text-center tabular-nums text-base text-purple-600">
          {formatQuantity(localNeededWithBuffer)} {item.unit}
        </td>
      </tr>
    )
  }

  // ── Shared sauce: combined row + per-dish breakdown ──
  return (
    <>
      {/* Main combined row */}
      <tr className="bg-purple-50/40 border-b border-purple-100">
        {/* Sauce Name with shared badge */}
        <td className="px-3 py-3 text-sm">
          <div className="flex items-center gap-2">
            {hasRecipe ? (
              <button onClick={handleSauceClick}
                className="font-semibold text-base text-purple-700 hover:text-purple-900 hover:underline"
                title={`View ${item.display_name || item.ingredient_name} recipe`}
              >{item.display_name || item.ingredient_name}</button>
            ) : (
              <div className="font-semibold text-base text-purple-700">{item.display_name || item.ingredient_name}</div>
            )}
            <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-purple-500 bg-purple-100 px-1.5 py-0.5 rounded">
              {item.dishes.length} dishes
            </span>
          </div>
          {purchaseName && <div className="text-xs text-havn-gray-500 mt-0.5">Purchase as: {purchaseName}</div>}
        </td>
        {/* Meal — show "Combined" */}
        <td className="px-3 py-3 text-center">
          <div className="text-xs font-medium text-purple-500 uppercase tracking-wide">Combined</div>
        </td>
        {/* Total Meal Count */}
        <td className="px-3 py-3 text-center tabular-nums text-base font-semibold text-purple-700">
          {item.total_meal_count || 0}
        </td>
        {/* Lbs/Meal — dash for combined row */}
        <td className="px-3 py-3 text-center text-havn-gray-400">—</td>
        {/* Total Needed */}
        <td className="px-3 py-3 text-center tabular-nums text-base font-semibold text-purple-700">
          {formatQuantity(localNeededWithBuffer)} {item.unit}
        </td>
      </tr>

      {/* Per-dish breakdown rows */}
      {computedDishes.map((dish) => (
        <tr key={dish.dish_id} className="bg-purple-50/15 hover:bg-purple-50/30 transition-colors border-b border-havn-cream-200/60">
          {/* Dish name — indented */}
          <td className="px-3 py-2 text-sm">
            <div className="flex items-center gap-1.5 pl-4">
              <span className="text-purple-300 text-xs">↳</span>
              <span className="text-havn-gray-600 text-sm">{dish.dish_name}</span>
            </div>
          </td>
          {/* Meal — empty for sub-rows */}
          <td className="px-3 py-2 text-center">
            <span className="text-xs text-havn-gray-400">{dish.dish_category}</span>
          </td>
          {/* Per-dish meal count */}
          <td className="px-3 py-2 text-center tabular-nums text-sm text-havn-gray-500">
            {dish.meal_count || 0}
          </td>
          {/* Per-dish lbs/meal — editable */}
          <td className="px-3 py-2 text-center">
            <input type="number"
              value={dishLbsPerMeal[dish.dish_id] || 0}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0
                setDishLbsPerMeal(prev => ({ ...prev, [dish.dish_id]: val }))
              }}
              onBlur={(e) => {
                const val = parseFloat(e.target.value) || 0
                if (val !== (dish.lbs_per_meal || 0)) handleLbsPerMealChange(val)
              }}
              onFocus={(e) => e.target.select()} step="0.05" min="0"
              className={`w-16 text-center text-xs tabular-nums rounded border px-1.5 py-1 transition-colors
                ${saving ? 'border-yellow-400 bg-yellow-50' : 'border-havn-cream-300 bg-white/80 hover:border-purple-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'}`}
            />
          </td>
          {/* Per-dish needed */}
          <td className="px-3 py-2 text-center tabular-nums text-sm text-purple-400">
            {formatQuantity(dish.localNeededWithBuffer)} {item.unit}
          </td>
        </tr>
      ))}
    </>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// INGREDIENT ROW - Now properly handles is_additional flag
// ══════════════════════════════════════════════════════════════════════════════

function IngredientRow({
  item,
  visibleColumns,
  cityId,
  menuId,
  onRefresh,
  onOpenDetail,
  splitOrders,
  onSplitOrdersChange,
  onViewRecipe,
  onViewSourceRecipe,
  onViewDish,
  suppliers,
  orderedStep = 10,
  onDataChange,  // Called after any successful save to trigger snapshot
  onFactorChange,
  onItemPatch    // Folds a saved value back into `data` so it survives a collapse
}) {
  const [showSplitOrders, setShowSplitOrders] = useState(splitOrders?.length > 0)
  const [saving, setSaving] = useState(false)
  const { error, success } = useToastActions()

  // Is this an additional ingredient (from recipes)?
  const isAdditional = item.is_additional === true
  const isProtein = item.component_type === 'protein'

  // Track whether "Used In" is expanded
  const [usedInExpanded, setUsedInExpanded] = useState(false)
  const rowRef = useRef(null)

  // Editable protein ingredient name
  const [editingName, setEditingName] = useState(false)
  const [localIngredientName, setLocalIngredientName] = useState(item.ingredient_name)
  const nameInputRef = useRef(null)

  // Focus input when editing starts
  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [editingName])

  async function handleNameSave() {
    const newName = localIngredientName.trim()
    if (!newName || newName === item.ingredient_name) {
      setLocalIngredientName(item.ingredient_name)
      setEditingName(false)
      return
    }
    try {
      setSaving(true)
      await orderingAPI.updateRawIngredientName(item.ingredient_name, newName)
      success(`Renamed to "${newName}"`)
      setEditingName(false)
      onRefresh?.()
    } catch (err) {
      error('Failed to rename ingredient')
      setLocalIngredientName(item.ingredient_name)
      setEditingName(false)
    } finally {
      setSaving(false)
    }
  }

  // LOCAL STATE for immediate UI updates (no page refresh needed)
  const [localLbsPerMeal, setLocalLbsPerMeal] = useState(item.lbs_per_meal || 0)
  const [localOnHand, setLocalOnHand] = useState(item.on_hand || 0)
  const [localOrdered, setLocalOrdered] = useState(item.ordered || 0)
  const [localIsOnHand, setLocalIsOnHand] = useState(item.is_on_hand || false)
  const [localSupplier, setLocalSupplier] = useState(item.supplier_name || '')

  // Sync local state with prop when item changes (e.g., after menu switch)
  useEffect(() => {
    setLocalOnHand(item.on_hand || 0)
    setLocalOrdered(item.ordered || 0)
    setLocalIsOnHand(item.is_on_hand || false)
    setLocalSupplier(item.supplier_name || '')
  }, [item.ingredient_id, item.on_hand, item.ordered, item.is_on_hand, item.supplier_name])

  useEffect(() => {
    setLocalLbsPerMeal(item.lbs_per_meal || 0)
  }, [item.ingredient_id, item.lbs_per_meal])

  // Compute needed and to_order locally based on local lbs_per_meal and on_hand.
  const { raw: localNeededRaw, withBuffer: localNeededWithBuffer, toOrder: localToOrder } =
    orderingQuantities(item, localLbsPerMeal, localOnHand)

  // Click outside to collapse "Used In" expansion
  useEffect(() => {
    if (!usedInExpanded) return

    function handleClickOutside(e) {
      if (rowRef.current && !rowRef.current.contains(e.target)) {
        setUsedInExpanded(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [usedInExpanded])

  // Calculate total ordered from split orders
  const totalOrdered = useMemo(() => {
    if (!splitOrders?.length) return 0
    return splitOrders.reduce((sum, order) => {
      const caseTotal = (order.cases || 0) * (order.case_size || 0)
      return sum + (caseTotal > 0 ? caseTotal : (order.amount || 0))
    }, 0)
  }, [splitOrders])

  const remaining = Math.max(0, localToOrder - totalOrdered)
  const isFulfilled = totalOrdered >= localToOrder
  const ingredientId = item.id || item.ingredient_id

  // Handle on_hand change - save to backend silently, update local state immediately
  async function handleOnHandChange(newValue) {
    setLocalOnHand(newValue) // Update UI immediately
    try {
      setSaving(true)
      await orderingAPI.updateOnHand(menuId, ingredientId, newValue, item.unit)
      onItemPatch?.(ingredientId, { on_hand: newValue })
      onDataChange?.() // Trigger snapshot save
    } catch (err) {
      error('Failed to update')
      setLocalOnHand(item.on_hand || 0) // Revert on error
    } finally {
      setSaving(false)
    }
  }

  // Handle supplier change - save to backend silently, update local state immediately
  async function handleSupplierChange(newSupplier) {
    setLocalSupplier(newSupplier) // Update UI immediately
    try {
      setSaving(true)
      await orderingAPI.updateSupplier(ingredientId, cityId, newSupplier)
      onItemPatch?.(ingredientId, { supplier_name: newSupplier })
      onDataChange?.() // Trigger snapshot save
    } catch (err) {
      error('Failed to update supplier')
      setLocalSupplier(item.supplier_name || '') // Revert on error
    } finally {
      setSaving(false)
    }
  }

  // Handle ordered amount change - save to backend silently, update local state immediately
  async function handleOrderedChange(newValue) {
    setLocalOrdered(newValue) // Update UI immediately (including color)
    try {
      setSaving(true)
      await orderingAPI.updateAmountOrdered(menuId, ingredientId, newValue, localLbsPerMeal)
      onItemPatch?.(ingredientId, { ordered: newValue, order_factor_locked: true })
      onDataChange?.() // Trigger snapshot save
    } catch (err) {
      error('Failed to update')
      setLocalOrdered(item.ordered || 0) // Revert on error
    } finally {
      setSaving(false)
    }
  }

  function handleLbsPerMealChange(newValue) {
    setLocalLbsPerMeal(newValue)
    onFactorChange(item, newValue)
  }

  // Handle is_on_hand toggle - save to backend silently, update local state immediately
  async function handleIsOnHandChange(newValue) {
    setLocalIsOnHand(newValue) // Update UI immediately
    try {
      setSaving(true)
      await orderingAPI.updateIsOnHand(menuId, ingredientId, newValue)
      onItemPatch?.(ingredientId, { is_on_hand: newValue })
      onDataChange?.() // Trigger snapshot save
    } catch (err) {
      error('Failed to update')
      setLocalIsOnHand(item.is_on_hand || false) // Revert on error
    } finally {
      setSaving(false)
    }
  }

  function addSplitOrder() {
    const newOrders = [...(splitOrders || []), {
      index: (splitOrders?.length || 0),
      cases: 0,
      case_size: 0,
      supplier: '',
    }]
    onSplitOrdersChange(ingredientId, newOrders)
    setShowSplitOrders(true)
  }

  function updateSplitOrder(index, updatedOrder) {
    const newOrders = [...(splitOrders || [])]
    newOrders[index] = updatedOrder
    onSplitOrdersChange(ingredientId, newOrders)
  }

  function removeSplitOrder(index) {
    const newOrders = (splitOrders || []).filter((_, i) => i !== index).map((o, i) => ({ ...o, index: i }))
    onSplitOrdersChange(ingredientId, newOrders)
    if (newOrders.length === 0) setShowSplitOrders(false)
  }

  // Keep columns aligned when a purchasing group mixes components and recipe inputs.
  const columnsToRender = visibleColumns

  // Row should be grayed out if is_on_hand is true (we have plenty) - use local state
  const isGrayedOut = localIsOnHand

  return (
    <tbody ref={rowRef}>
      <tr className={`group transition-colors border-b border-havn-cream-200 ${saving ? 'opacity-60' : ''} ${
        isGrayedOut
          ? 'bg-havn-cream-100/50 text-havn-gray-400'
          : 'hover:bg-havn-cream-50'
      }`}>
        {columnsToRender.map(col => (
          <td key={col.id} className="px-3 py-3 text-sm">
            {col.id === 'ingredient' && (
              <div className="flex items-center gap-1">
                {editingName ? (
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={localIngredientName}
                    onChange={e => setLocalIngredientName(e.target.value)}
                    onBlur={handleNameSave}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleNameSave()
                      if (e.key === 'Escape') {
                        setLocalIngredientName(item.ingredient_name)
                        setEditingName(false)
                      }
                    }}
                    className="font-medium text-base text-havn-charcoal bg-white border border-havn-green-300 rounded px-2 py-0.5 outline-none focus:ring-2 focus:ring-havn-green-300 w-full"
                  />
                ) : (
                  <span className="flex flex-col items-start">
                    <button
                      onClick={() => onOpenDetail(item)}
                      className={`font-medium transition-colors text-left text-base ${
                        isGrayedOut
                          ? 'text-havn-gray-400 hover:text-havn-gray-600'
                          : 'text-havn-charcoal hover:text-havn-green-700 hover:underline'
                      }`}
                    >
                      {item.ingredient_name}
                    </button>
                    {item.purchase_names?.length > 0 && (
                      <span className="text-xs text-havn-gray-500">Buy as {item.purchase_names.join(' + ')}</span>
                    )}
                    {/* Protein name edit button removed — per-dish rows show the correct dish name directly */}
                  </span>
                )}
              </div>
            )}
            {col.id === 'used_in' && (
              <UsedInCell
                components={item.components}
                sourceRecipes={item.source_recipes}
                dishes={item.dishes}
                isExpanded={usedInExpanded}
                onToggleExpand={() => setUsedInExpanded(!usedInExpanded)}
                onViewRecipe={onViewSourceRecipe}
              />
            )}
            {col.id === 'meal' && !isAdditional && item.dishes && (
              <MealCell
                dishes={item.dishes}
                onViewDish={onViewDish}
              />
            )}
            {col.id === 'meal_count' && !isAdditional && (
              <div className="text-center tabular-nums font-medium text-havn-green-700 text-base">
                {item.total_meal_count || 0}
              </div>
            )}
            {col.id === 'lbs_per_meal' && !isAdditional && (
              <div className="w-20 mx-auto">
                <InlineEditCell
                  value={localLbsPerMeal}
                  onChange={handleLbsPerMealChange}
                  className="text-center text-base"
                  step={0.01}
                  decimals={2}
                />
                {item.order_factor_saving ? (
                  <div role="status" className="mt-1 text-[10px] text-amber-700 text-center">Saving…</div>
                ) : item.order_factor_locked && (
                  <div className="mt-1 text-[10px] leading-tight text-havn-green-700 text-center" title="This value is frozen for this menu and will not change when another week is edited.">
                    Locked this menu
                  </div>
                )}
                {item.order_factor_portion_oz && (
                  <div className="mt-1 text-[10px] leading-tight text-havn-gray-500 text-center"
                    title={(item.dishes || []).map((d) => `${d.dish_name}: ${d.meal_count} × ${d.portion_oz ?? 6} oz`).join('\n')}>
                    {item.legacy_chicken_allowance ? 'legacy lb / 6 oz' : 'raw lb / 6 oz'}<br />4 oz uses ⅔
                  </div>
                )}
                {item.legacy_chicken_allowance && (
                  <p className="mt-1 text-[10px] text-amber-700">Custom allowance preserved. Editing sets raw lb per 6 oz, including cooking loss.</p>
                )}
              </div>
            )}
            {col.id === 'needed' && (
              <div className={`text-center tabular-nums text-base ${isGrayedOut ? 'text-havn-gray-400' : ''}`}>
                {formatQuantity(convertFromLbs(isAdditional ? item.needed_with_buffer : localNeededWithBuffer, item.display_unit))} {getUnitLabel(item.display_unit || item.unit)}
              </div>
            )}
            {col.id === 'on_hand' && (
              <OnHandEditCell
                value={convertFromLbs(localOnHand, item.display_unit)}
                onChange={(newValue) => handleOnHandChange(convertToLbs(newValue, item.display_unit))}
                unit={getUnitLabel(item.display_unit || item.unit)}
                isOnHand={localIsOnHand}
                onIsOnHandChange={handleIsOnHandChange}
                disabled={localIsOnHand}
              />
            )}
            {col.id === 'to_order' && (
              <div className="text-center">
                <div className={`font-semibold tabular-nums text-base
                  ${isGrayedOut ? 'text-havn-gray-400' : localToOrder > 0 ? 'text-havn-green-700' : 'text-havn-gray-400'}`}>
                  {localIsOnHand ? '—' : `${formatQuantity(convertFromLbs(isAdditional ? item.to_order : localToOrder, item.display_unit))} ${getUnitLabel(item.display_unit || item.unit)}`}
                </div>
                {!localIsOnHand && splitOrders?.length > 0 && (
                  <div className={`text-xs mt-0.5 ${isFulfilled ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {isFulfilled ? 'Fulfilled' : `${formatQuantity(convertFromLbs(remaining, item.display_unit))} remaining`}
                  </div>
                )}
              </div>
            )}
            {col.id === 'ordered' && !isGrayedOut && (
              <div className="text-center">
                {splitOrders?.length > 0 ? (
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1">
                      <div className={`px-2 py-1 rounded text-sm font-medium tabular-nums ${getOrderedStatusColor(totalOrdered, localToOrder) || ''}`}>
                        {formatQuantity(convertFromLbs(totalOrdered, item.display_unit))} {getUnitLabel(item.display_unit || item.unit)}
                      </div>
                      <button
                        onClick={() => setShowSplitOrders(!showSplitOrders)}
                        className={`p-1 rounded transition-colors ${showSplitOrders ? 'text-purple-700 bg-purple-100' : 'text-purple-500 hover:text-purple-700 hover:bg-purple-50'}`}
                      >
                        <Split className="w-3.5 h-3.5" />
                        <span className="text-xs ml-0.5">{splitOrders.length}</span>
                      </button>
                    </div>
                    <button
                      onClick={addSplitOrder}
                      className="p-1 text-havn-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-1">
                    {(() => {
                      const statusColor = getOrderedStatusColor(localOrdered, localToOrder)
                      const textColorClass = statusColor ? statusColor.split(' ').filter(c => c.startsWith('text-')).join(' ') : ''
                      const displayUnit = getUnitLabel(item.display_unit || item.unit)
                      return (
                        <div className={`w-24 rounded ${statusColor || ''}`}>
                          <InlineEditCell
                            value={convertFromLbs(localOrdered, item.display_unit)}
                            onChange={(newValue) => handleOrderedChange(convertToLbs(newValue, item.display_unit))}
                            suffix={localOrdered ? ` ${displayUnit}` : ''}
                            className={`text-center text-base ${statusColor ? 'font-semibold ' + textColorClass : ''}`}
                            step={orderedStep}
                          />
                        </div>
                      )
                    })()}
                    <button
                      onClick={addSplitOrder}
                      className="p-1 text-havn-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
            {col.id === 'ordered' && isGrayedOut && (
              <div className="text-center text-havn-gray-400">—</div>
            )}
            {col.id === 'supplier' && (
              <SupplierDropdown
                value={localSupplier}
                productUrl={item.product_url}
                onChange={handleSupplierChange}
                suppliers={suppliers}
              />
            )}
            {col.id === 'notes' && (
              <div className="text-xs text-havn-gray-500 truncate max-w-[150px]" title={item.notes}>
                {item.notes || '-'}
              </div>
            )}
          </td>
        ))}

      </tr>

      {/* Used In Expansion Row */}
      {usedInExpanded && (
        <UsedInExpandedRow
          components={item.components}
          sourceRecipes={item.source_recipes}
          colSpan={columnsToRender.length}
          onViewRecipe={onViewSourceRecipe}
        />
      )}

      {/* Split Orders Expansion */}
      {showSplitOrders && splitOrders?.length > 0 && (
        <tr className="bg-purple-50/50">
          <td colSpan={visibleColumns.length} className="px-3 py-2">
            <div className="ml-6 p-3 bg-white rounded-lg border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs font-medium text-purple-700">
                  <Boxes className="w-4 h-4" />
                  Split Orders - Total: {formatQuantity(totalOrdered)} {item.unit}
                  {item.to_order > 0 && (
                    <span className={isFulfilled ? 'text-emerald-600' : 'text-amber-600'}>
                      ({isFulfilled ? 'Fulfilled' : `Need ${formatQuantity(remaining)} more`})
                    </span>
                  )}
                </div>
                <button
                  onClick={addSplitOrder}
                  className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                >
                  + Add Another
                </button>
              </div>
              <div className="space-y-1">
                {splitOrders.map((order, i) => (
                  <SplitOrderRow
                    key={i}
                    order={{ ...order, index: i }}
                    suppliers={SUPPLIERS}
                    onUpdate={(updated) => updateSplitOrder(i, updated)}
                    onRemove={() => removeSplitOrder(i)}
                    isLast={i === splitOrders.length - 1}
                  />
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </tbody>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ORDER TABLE SECTION
// ══════════════════════════════════════════════════════════════════════════════

function OrderTable({
  title,
  categoryKey,
  items,
  columns,
  cityId,
  menuId,
  onRefresh,
  onOpenDetail,
  splitOrders,
  onSplitOrdersChange,
  forceExpanded,
  onViewRecipe,
  onViewSourceRecipe,
  onViewDish,
  suppliers,
  orderedStep = 10,
  isAdditional = false,
  onDataChange,  // Called when any data changes to trigger snapshot
  onFactorChange,
  onItemPatch    // Forwarded to rows so a saved edit survives collapse/expand
}) {
  const [expanded, setExpanded] = useState(true)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (forceExpanded !== undefined && !isFirstRender.current) {
      setExpanded(forceExpanded)
    }
    isFirstRender.current = false
  }, [forceExpanded])

  const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.dry_goods
  const Icon = config.icon

  const visibleColumns = columns.filter(c => c.visible)
  const totalToOrder = items.reduce((sum, item) => sum + (item.to_order || 0), 0)

  const totalOrdered = useMemo(() => {
    return items.reduce((sum, item) => {
      const itemId = item.id || item.ingredient_id
      const itemSplitOrders = splitOrders[itemId] || []

      if (itemSplitOrders.length > 0) {
        return sum + itemSplitOrders.reduce((orderSum, order) => {
          const caseTotal = (order.cases || 0) * (order.case_size || 0)
          return orderSum + (caseTotal > 0 ? caseTotal : (order.amount || 0))
        }, 0)
      }

      const directAmount = typeof item.ordered === 'number' ? item.ordered : 0
      return sum + directAmount
    }, 0)
  }, [items, splitOrders])

  if (items.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-havn-cream-300 mb-4 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-havn-cream-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-havn-charcoal">{title}</h3>
            <p className="text-xs text-havn-gray-500">{items.length} items</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {totalToOrder > 0 && (
            <div className="text-right">
              <div className="text-xs text-havn-gray-400">Need / Ordered</div>
              <div className="font-semibold tabular-nums">
                <span className="text-havn-green-700">{formatQuantity(totalToOrder)}</span>
                <span className="text-havn-gray-400 mx-1">/</span>
                <span className={totalOrdered >= totalToOrder ? 'text-emerald-600' : 'text-amber-600'}>
                  {formatQuantity(totalOrdered)}
                </span>
                <span className="text-havn-gray-400 ml-1">lbs</span>
              </div>
            </div>
          )}
          {expanded ? <ChevronUp className="w-5 h-5 text-havn-gray-400" /> : <ChevronDown className="w-5 h-5 text-havn-gray-400" />}
        </div>
      </button>

      {/* Table */}
      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-havn-cream-100 border-t border-havn-cream-200">
              <tr>
                {visibleColumns.map(col => {
                  // Purchasing groups can contain both components and recipe inputs.
                  return (
                    <th key={col.id} className="px-3 py-3 text-center text-sm font-semibold text-havn-gray-600 whitespace-nowrap">
                      {col.label}
                    </th>
                  )
                })}
              </tr>
            </thead>
            {items.map((item, i) => (
              <IngredientRow
                key={`${menuId}:${item.ingredient_id || item.id || i}`}
                item={{ ...item, menu_id: menuId }}
                visibleColumns={visibleColumns}
                cityId={cityId}
                menuId={menuId}
                onRefresh={onRefresh}
                onOpenDetail={onOpenDetail}
                splitOrders={splitOrders[item.ingredient_id || item.id] || []}
                onSplitOrdersChange={onSplitOrdersChange}
                onViewRecipe={onViewRecipe}
                onViewSourceRecipe={onViewSourceRecipe}
                onViewDish={onViewDish}
                suppliers={suppliers}
                orderedStep={orderedStep}
                onDataChange={onDataChange}
                onItemPatch={onItemPatch}
                onFactorChange={onFactorChange}
              />
            ))}
          </table>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SAUCE TABLE
// ══════════════════════════════════════════════════════════════════════════════

function SauceTable({ title, items, forceExpanded, onViewRecipe, menuId, cityId, onDataChange, onItemPatch }) {
  const [expanded, setExpanded] = useState(true)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (forceExpanded !== undefined && !isFirstRender.current) {
      setExpanded(forceExpanded)
    }
    isFirstRender.current = false
  }, [forceExpanded])

  const config = CATEGORY_CONFIG.sauces
  const Icon = config.icon
  const totalNeeded = items.reduce((sum, item) => sum + (item.needed_with_buffer || 0), 0)

  // Sauce-specific columns (simpler than main ingredients)
  const sauceColumns = [
    { id: 'sauce', label: 'Sauce' },
    { id: 'meal', label: 'Meal' },
    { id: 'meal_count', label: 'Meal Count' },
    { id: 'lbs_per_meal', label: 'Lbs/Meal' },
    { id: 'needed', label: 'Needed' },
  ]

  if (items.length === 0) return null

  return (
    <div className="bg-white rounded-xl border border-purple-200 mb-4 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-havn-cream-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center opacity-60`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-havn-charcoal">{title}</h3>
            <p className="text-xs text-havn-gray-500">{items.length} items</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs text-havn-gray-400">Total Needed</div>
            <div className="font-semibold tabular-nums text-havn-gray-500">
              {formatQuantity(totalNeeded)} lbs
            </div>
          </div>
          {expanded ? <ChevronUp className="w-5 h-5 text-havn-gray-400" /> : <ChevronDown className="w-5 h-5 text-havn-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-havn-cream-100 border-t border-havn-cream-200">
              <tr>
                {sauceColumns.map(col => (
                  <th key={col.id} className="px-3 py-3 text-center text-sm font-semibold text-havn-gray-600 whitespace-nowrap">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <SauceRow
                  key={item.ingredient_name || i}
                  item={item}
                  onViewRecipe={onViewRecipe}
                  menuId={menuId}
                  cityId={cityId}
                  onDataChange={onDataChange}
                  onItemPatch={onItemPatch}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// SUMMARY CARDS
// ══════════════════════════════════════════════════════════════════════════════

function SummaryCards({ data }) {
  const stats = useMemo(() => {
    const additionalLiquids = data.additional?.liquids || []
    const additionalDryGoods = data.additional?.dry_goods || []
    const additionalRefrigerated = data.additional?.refrigerated || []

    const allItems = [
      ...(data.proteins || []),
      ...(data.vegetables || []),
      ...(data.carbs || []),
      ...(data.bases || []),
      ...additionalLiquids,
      ...additionalDryGoods,
      ...additionalRefrigerated,
    ]

    const itemsToOrder = allItems.filter(i => i.to_order > 0 && !i.is_on_hand).length
    const totalLbsToOrder = allItems.reduce((sum, i) => sum + (i.to_order || 0), 0)
    const orderedCount = allItems.filter(i => i.ordered).length

    return {
      totalMeals: data.summary?.total_meals || 0,
      saucesCount: data.summary?.sauces_count || (data.sauces?.length || 0),
      additionalCount: data.summary?.additional_count || (additionalLiquids.length + additionalDryGoods.length + additionalRefrigerated.length),
      totalItems: allItems.length,
      itemsToOrder,
      totalLbsToOrder,
      orderedCount,
    }
  }, [data])

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
      <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
        <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Total Meals</div>
        <div className="text-2xl font-display font-semibold text-havn-green-900">{stats.totalMeals}</div>
      </div>
      <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
        <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Ingredients</div>
        <div className="text-2xl font-display font-semibold text-havn-green-900">{stats.totalItems}</div>
      </div>
      <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
        <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Items to Order</div>
        <div className="text-2xl font-display font-semibold text-amber-600">{stats.itemsToOrder}</div>
      </div>
      <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
        <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Total Lbs</div>
        <div className="text-2xl font-display font-semibold text-havn-green-700">{formatQuantity(stats.totalLbsToOrder)}</div>
      </div>
      <div className="bg-white rounded-xl border border-havn-cream-300 p-4">
        <div className="text-xs text-havn-gray-500 uppercase tracking-wide mb-1">Ordered</div>
        <div className="text-2xl font-display font-semibold text-emerald-600">{stats.orderedCount}/{stats.itemsToOrder}</div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN ORDERING PAGE
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// WELLNESS SHOTS SECTION — Shows per-shot production quantities
// ══════════════════════════════════════════════════════════════════════════════

const SHOT_TYPES = [
  { name: 'Immunity Gold Shot', color: 'bg-amber-400', emoji: '🟡' },
  { name: 'Endurance Red Shot', color: 'bg-red-500', emoji: '🔴' },
  { name: 'Purify Green Shot', color: 'bg-emerald-500', emoji: '🟢' },
  { name: 'Clarity Blue Shot', color: 'bg-blue-500', emoji: '🔵' },
]

function WellnessShotsSection({ items, summary }) {
  const bundles = summary?.total_wellness_bundles || 0
  if (bundles === 0 && (!items || items.length === 0)) return null

  return (
    <div className="mb-8">
      <h2 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
        Wellness Shots
        <span className="text-sm font-normal text-havn-gray-400 ml-2">
          ({bundles} {bundles === 1 ? 'bundle' : 'bundles'} — 4 shots per bundle, 2 oz each)
        </span>
      </h2>

      <div className="bg-white rounded-xl border border-havn-cream-300 shadow-soft overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-havn-cream-50 border-b border-havn-cream-200">
              <th className="text-left px-5 py-3 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">Shot</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">Bundles</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">Shots</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">Volume (oz)</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-havn-gray-500 uppercase tracking-wider">Volume (cups)</th>
            </tr>
          </thead>
          <tbody>
            {SHOT_TYPES.map((shot, i) => {
              const shots = bundles
              const oz = shots * 2
              const cups = (oz / 8).toFixed(1)
              return (
                <tr key={shot.name} className={i % 2 === 0 ? 'bg-white' : 'bg-havn-cream-50/50'}>
                  <td className="px-5 py-3 flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${shot.color} inline-block flex-shrink-0`} />
                    <span className="font-medium text-havn-charcoal text-sm">{shot.name}</span>
                  </td>
                  <td className="text-right px-5 py-3 tabular-nums text-sm text-havn-gray-600">{bundles}</td>
                  <td className="text-right px-5 py-3 tabular-nums text-sm font-semibold text-havn-charcoal">{shots}</td>
                  <td className="text-right px-5 py-3 tabular-nums text-sm text-havn-gray-600">{oz} oz</td>
                  <td className="text-right px-5 py-3 tabular-nums text-sm text-havn-gray-600">{cups} cups</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className="bg-havn-green-800 text-white">
              <td className="px-5 py-3 font-semibold text-sm">Total</td>
              <td className="text-right px-5 py-3 tabular-nums text-sm">{bundles}</td>
              <td className="text-right px-5 py-3 tabular-nums text-sm font-bold">{bundles * 4}</td>
              <td className="text-right px-5 py-3 tabular-nums text-sm font-bold">{bundles * 4 * 2} oz</td>
              <td className="text-right px-5 py-3 tabular-nums text-sm">{((bundles * 4 * 2) / 8).toFixed(1)} cups</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-havn-gray-400 mt-2 ml-1">
        Shot ingredients are included in Pantry & refrigerated below, linked to each shot recipe.
      </p>
    </div>
  )
}

export default function Ordering() {
  const { currentMenu, currentCity, isSpanish } = useApp()
  const [data, setData] = useState(null)
  const sync = useRef(createOrderingSync()).current
  const loadRequest = useRef(0)
  const activeMenu = useRef(currentMenu)
  useLayoutEffect(() => {
    activeMenu.current = currentMenu
    sync.invalidate()
    return () => { activeMenu.current = null; sync.invalidate() }
  }, [currentMenu, sync])
  const [loading, setLoading] = useState(true)
  const [columns, setColumns] = useState(DEFAULT_COLUMNS)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState('all')
  const [showColumnSettings, setShowColumnSettings] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedIngredient, setSelectedIngredient] = useState(null)
  const [splitOrders, setSplitOrders] = useState({})
  const [allExpanded, setAllExpanded] = useState(true)
  const [viewingRecipe, setViewingRecipe] = useState(null)
  const [viewingDish, setViewingDish] = useState(null)  // For viewing dish details
  const [citySuppliers, setCitySuppliers] = useState(SUPPLIERS)  // City-specific suppliers
  const [showRecoveryModal, setShowRecoveryModal] = useState(false)
  const [snapshots, setSnapshots] = useState([])
  const [loadingSnapshots, setLoadingSnapshots] = useState(false)
  const [inventoryItems, setInventoryItems] = useState([])
  const { success, error } = useToastActions()

  // Fetch suppliers when city changes
  useEffect(() => {
    async function loadSuppliers() {
      if (!currentCity) return
      try {
        const suppliers = await suppliersAPI.getForCity(currentCity)
        if (suppliers && suppliers.length > 0) {
          setCitySuppliers(suppliers)
        }
      } catch (err) {
        console.warn('Failed to load city suppliers, using defaults:', err)
      }
    }
    loadSuppliers()
  }, [currentCity])

  // Undo/Redo
  const {
    pushState: pushUndoState,
    undo,
    redo,
    canUndo,
    canRedo,
    getUndoDescription,
    getRedoDescription
  } = useUndoRedo()

  // Version history
  const {
    versions,
    saveVersion,
    revertToVersion,
    showVersionModal,
    setShowVersionModal
  } = useVersionHistory(currentMenu)

  // Auto-save ref
  const autoSaveRef = useRef(null)
  const lastDataRef = useRef(null)
  const isInitialLoad = useRef(true)

  const handleSplitOrdersChange = useCallback((ingredientId, orders) => {
    setSplitOrders(prev => ({
      ...prev,
      [ingredientId]: orders
    }))
  }, [])

  const handleViewRecipe = useCallback((recipeIdOrDish, recipeName, targetQuantity, targetCount) => {
    if (typeof recipeIdOrDish === 'object' && recipeIdOrDish.name) {
      const dish = recipeIdOrDish
      setViewingRecipe({
        dishId: dish.dish_id,
        recipeName: dish.name,
        targetQuantity: dish.count || 10,
        isDish: true
      })
    } else {
      setViewingRecipe({ recipeId: recipeIdOrDish, recipeName, targetQuantity, targetCount })
    }
  }, [])

  const handleViewSourceRecipe = useCallback((recipeId, recipeName, targetQuantity, targetCount) => {
    if (!recipeId) return
    setViewingRecipe({
      recipeId,
      recipeName,
      targetCount,
      targetQuantity: targetQuantity || 10,
      isDish: false
    })
  }, [])

  // View a dish/meal in the scaled recipe modal
  const handleViewDish = useCallback((dish) => {
    if (!dish) return
    setViewingRecipe({
      dishId: dish.dish_id,
      recipeName: dish.dish_name,
      targetQuantity: dish.meal_count || 10,
      isDish: true
    })
  }, [])

  // Load data from backend - this is the SINGLE SOURCE OF TRUTH
  const loadData = useCallback(async () => {
    const request = ++loadRequest.current
    const isCurrent = () => activeMenu.current === currentMenu && loadRequest.current === request
    if (!currentMenu) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      sync.invalidate()
      let orderingResult
      // A save may finish while a refresh is in flight. Fetch again rather than
      // exposing the pre-save response (including when switching menus).
      while (isCurrent()) {
        await sync.whenIdle()
        if (!isCurrent()) return
        const readRevision = sync.capture()
        orderingResult = await orderingAPI.calculate(currentMenu)
        if (!isCurrent()) return
        if (sync.canRefresh(readRevision)) break
      }
      if (!isCurrent()) return
      sync.invalidate()
      setData(orderingResult)

      // Also load inventory items (for the Inventory section)
      try {
        const inv = await inventoryAPI.getItems(currentMenu)
        if (isCurrent()) setInventoryItems(inv.items || [])
      } catch (invErr) {
        // Non-critical — inventory section just won't show
        console.warn('Failed to load inventory items:', invErr)
      }

      // Push to undo history
      if (isInitialLoad.current) {
        pushUndoState(orderingResult, 'Initial state')
        isInitialLoad.current = false
      }
    } catch (err) {
      if (isCurrent()) error('Failed to load ordering data')
      console.error(err)
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [currentMenu, error, pushUndoState, sync])

  // Load data when menu changes
  useEffect(() => {
    loadData()
  }, [currentMenu]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Multi-user sync: poll for changes every 30 seconds ──
  const lastKnownHash = useRef(null)
  const lastLocalEditRef = useRef(0)  // ms timestamp of our most recent local save
  useEffect(() => {
    if (!currentMenu) return

    // The server hash changes on EVERY edit, including our own. A change within
    // this window of our last local save is ours — adopting it (no refetch) is
    // what stops the phantom "another user changed the numbers" + value-revert.
    const SELF_EDIT_WINDOW = 10000
    let active = true
    let polling = false
    lastKnownHash.current = null
    const isEditing = () => {
      const ae = document.activeElement
      return !!ae && (ae.tagName === 'INPUT' || ae.tagName === 'SELECT' || ae.isContentEditable)
    }

    const pollForChanges = async () => {
      if (polling) return
      polling = true
      try {
        const readRevision = sync.capture()
        const status = await orderingAPI.lastModified(currentMenu)
        if (!active || !sync.canRefresh(readRevision)) return
        if (lastKnownHash.current && status.hash !== lastKnownHash.current) {
          const isTyping = isEditing()
          if (Date.now() - lastLocalEditRef.current < SELF_EDIT_WINDOW) {
            // Our own just-saved edit moved the hash — adopt it, never refetch over
            // the value the operator is still working on.
          } else if (isTyping) {
            // A genuine external change, but the operator is mid-entry. Defer: leave
            // the baseline unchanged so we retry next poll, instead of wiping their
            // in-progress input.
            return
          } else {
            // A real change from another user — pull it in.
            console.log('[sync] Data changed by another user, refreshing...')
            const orderingResult = await orderingAPI.calculate(currentMenu)
            if (!active || !sync.canRefresh(readRevision) || isEditing()) return
            sync.invalidate()
            setData(orderingResult)
            success('Data updated by another user')
          }
        }
        lastKnownHash.current = status.hash
      } catch (err) {
        // Silently ignore polling errors — non-critical
        console.warn('[sync] Polling failed:', err.message)
      } finally {
        polling = false
      }
    }

    // Initial hash fetch (no refresh, just set baseline)
    pollForChanges()

    const intervalId = setInterval(pollForChanges, 30000) // 30 seconds
    return () => { active = false; clearInterval(intervalId) }
  }, [currentMenu]) // eslint-disable-line react-hooks/exhaustive-deps

  // Ref for database auto-save interval
  const dbAutoSaveRef = useRef(null)
  const lastDbSaveRef = useRef(null)

  // Auto-save to DATABASE every 30 seconds (bulletproof backup)
  useEffect(() => {
    if (!data || !currentMenu) return

    // Function to save snapshot to database
    const saveToDatabase = async () => {
      const currentDataStr = JSON.stringify(data)
      if (lastDbSaveRef.current === currentDataStr) return  // No changes

      try {
        await orderingAPI.createSnapshot(currentMenu, data, getBrowserId())
        lastDbSaveRef.current = currentDataStr
        console.log('[Auto-save] Snapshot saved to database')
      } catch (err) {
        console.error('[Auto-save] Failed to save snapshot:', err)
      }
    }

    // Clear existing interval
    if (dbAutoSaveRef.current) {
      clearInterval(dbAutoSaveRef.current)
    }

    // Save immediately on first load and when data changes significantly
    if (!lastDbSaveRef.current) {
      saveToDatabase()
    }

    // Set up interval for periodic saves
    dbAutoSaveRef.current = setInterval(saveToDatabase, AUTO_SAVE_INTERVAL)

    // Also save when user leaves the page
    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable save on page close
      const payload = JSON.stringify({
        ordering_data: data,
        user_identifier: getBrowserId()
      })
      navigator.sendBeacon(`/api/ordering/snapshot/${currentMenu}`, payload)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      if (dbAutoSaveRef.current) {
        clearInterval(dbAutoSaveRef.current)
      }
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [data, currentMenu])

  // Auto-save LOCAL version history every 2 minutes (for undo/redo)
  useEffect(() => {
    if (!data || !currentMenu) return

    if (autoSaveRef.current) {
      clearInterval(autoSaveRef.current)
    }

    autoSaveRef.current = setInterval(() => {
      const currentDataStr = JSON.stringify(data)
      if (lastDataRef.current !== currentDataStr) {
        saveVersion(data, 'Auto-save')
        lastDataRef.current = currentDataStr
      }
    }, LOCAL_VERSION_INTERVAL)

    if (!lastDataRef.current) {
      lastDataRef.current = JSON.stringify(data)
      saveVersion(data, 'Initial load')
    }

    return () => {
      if (autoSaveRef.current) {
        clearInterval(autoSaveRef.current)
      }
    }
  }, [data, currentMenu, saveVersion])

  const handleUndo = useCallback(() => {
    const previousState = undo()
    if (previousState) {
      setData(previousState)
      success('Undone')
    }
  }, [undo, success])

  const handleRedo = useCallback(() => {
    const nextState = redo()
    if (nextState) {
      setData(nextState)
      success('Redone')
    }
  }, [redo, success])

  const handleRevertToVersion = useCallback((versionId) => {
    const versionData = revertToVersion(versionId)
    if (versionData) {
      setData(versionData)
      setShowVersionModal(false)
      success('Reverted to previous version')
    }
  }, [revertToVersion, setShowVersionModal, success])

  // Load snapshots for recovery modal
  const loadSnapshots = useCallback(async () => {
    if (!currentMenu) return
    setLoadingSnapshots(true)
    try {
      const snapshotList = await orderingAPI.listSnapshots(currentMenu, 30)
      setSnapshots(snapshotList)
    } catch (err) {
      console.error('Failed to load snapshots:', err)
      error('Failed to load recovery points')
    } finally {
      setLoadingSnapshots(false)
    }
  }, [currentMenu, error])

  // Handle recovery modal open
  const handleOpenRecovery = useCallback(() => {
    setShowRecoveryModal(true)
    loadSnapshots()
  }, [loadSnapshots])

  // Restore from a snapshot
  const handleRestoreSnapshot = useCallback(async (snapshotId) => {
    if (!currentMenu) return
    try {
      await orderingAPI.restoreSnapshot(currentMenu, snapshotId)
      setShowRecoveryModal(false)
      success('Data restored successfully! Refreshing...')
      // Reload the data
      await loadData()
    } catch (err) {
      console.error('Failed to restore snapshot:', err)
      error('Failed to restore data')
    }
  }, [currentMenu, loadData, success, error])

  // Debounced snapshot save ref to avoid too many saves
  const snapshotDebounceRef = useRef(null)

  // Fold a saved cell edit back into `data`.
  //
  // Rows keep their edited value in component state and write it to the server,
  // but nothing used to update `data`. Collapsing a category unmounts its rows,
  // so that state was destroyed and re-expanding rebuilt every row from the
  // stale `item` prop — the operator's entries appeared to vanish even though
  // the server had them. It also meant the 30s snapshot serialised pre-edit
  // values. Patching `data` on each successful save fixes both.
  const patchItem = useCallback((ingredientId, patch) => {
    if (ingredientId == null || !patch) return
    sync.invalidate()
    const applyToList = (list) => {
      let hit = false
      const out = list.map((it) => {
        if ((it.ingredient_id ?? it.id) !== ingredientId) return it
        hit = true
        return { ...it, ...patch }
      })
      return hit ? out : null
    }
    setData((prev) => {
      if (!prev) return prev
      let changed = false
      const next = { ...prev }
      for (const [key, val] of Object.entries(prev)) {
        if (Array.isArray(val)) {
          const out = applyToList(val)
          if (out) { next[key] = out; changed = true }
        } else if (val && typeof val === 'object') {
          // `additional` is a dict of category -> array of ingredients
          let groupChanged = false
          const group = { ...val }
          for (const [gk, gv] of Object.entries(val)) {
            if (!Array.isArray(gv)) continue
            const out = applyToList(gv)
            if (out) { group[gk] = out; groupChanged = true }
          }
          if (groupChanged) { next[key] = group; changed = true }
        }
      }
      return changed ? next : prev
    })
  }, [])

  // Handle any data change - save snapshot + refresh inventory (debounced)
  const handleDataChange = useCallback(() => {
    if (!currentMenu || !data) return

    // Mark that WE just changed the data so the multi-user poll treats the
    // resulting hash change as our own (not "another user") for a short window.
    lastLocalEditRef.current = Date.now()

    // Clear any pending debounced save
    if (snapshotDebounceRef.current) {
      clearTimeout(snapshotDebounceRef.current)
    }

    // Debounce by 500ms to batch rapid changes (e.g., someone typing quickly)
    snapshotDebounceRef.current = setTimeout(async () => {
      try {
        await orderingAPI.createSnapshot(currentMenu, data, getBrowserId())
        console.log('[Data Change] Snapshot saved')
      } catch (err) {
        console.error('[Data Change] Failed to save snapshot:', err)
      }
      // Adopt the post-edit server hash as the new sync baseline, so a later poll
      // (beyond the self-edit window) doesn't see our own change as another user's.
      try {
        const status = await orderingAPI.lastModified(currentMenu)
        lastKnownHash.current = status.hash
      } catch (e) { /* non-critical */ }
      // Also refresh inventory items for two-way sync
      // (ordering row edits update menu_ordering → inventory reads from menu_ordering)
      try {
        const inv = await inventoryAPI.getItems(currentMenu)
        setInventoryItems(inv.items || [])
      } catch (invErr) {
        // Non-critical
      }
    }, 500)
  }, [currentMenu, data])

  // A searchable label for one entry of an item's `dishes` / `source_recipes` array.
  // The ordering API emits dishes as objects keyed `dish_name` (see ordering_v2.py),
  // NOT `name` — so reading `.name` yields undefined, and the old `(d.name || d)`
  // fell through to the OBJECT and threw on .toLowerCase(), blanking the whole page
  // the moment anyone typed in the search box. Coerce, never assume a string.
  const searchLabel = (entry) => {
    if (typeof entry === 'string') return entry
    if (!entry || typeof entry !== 'object') return ''
    return String(entry.dish_name ?? entry.name ?? entry.recipe_name ?? '')
  }

  // Explicit factor edits replace this menu's saved override, in submission order.
  const handleFactorChange = useCallback((item, value) => {
    const ingredientId = item.ingredient_id ?? item.id
    if (ingredientId == null) return
    lastLocalEditRef.current = Date.now()
    return sync.save({
      key: `${currentMenu}:${ingredientId}`,
      value: {
        lbs_per_meal: value,
        order_factor_locked: true,
        legacy_chicken_allowance: item.order_factor_portion_oz ? false : item.legacy_chicken_allowance,
      },
      previous: {
        lbs_per_meal: item.lbs_per_meal || 0,
        order_factor_locked: item.order_factor_locked,
        legacy_chicken_allowance: item.legacy_chicken_allowance,
      },
      write: (next) => orderingAPI.updateOrderFactor(ingredientId, next.lbs_per_meal, currentCity, currentMenu),
      apply: (next, pending) => {
        if (activeMenu.current !== currentMenu) return
        patchItem(ingredientId, {
          ...next,
          order_factor_saving: pending,
        })
        if (!pending) handleDataChange()
      },
      onError: () => {
        if (activeMenu.current === currentMenu) error('Ordering factor was not saved. Restored the last saved value.')
      },
    })
  }, [currentMenu, currentCity, sync, patchItem, handleDataChange, error])

  // Filter items
  const filteredData = useMemo(() => {
    if (!data) return data

    const matchesSearch = (i, term) =>
      i.ingredient_name?.toLowerCase().includes(term) ||
      i.supplier_name?.toLowerCase().includes(term) ||
      i.used_in_recipes?.some(name => name.toLowerCase().includes(term)) ||
      i.dishes?.some(d => searchLabel(d).toLowerCase().includes(term)) ||
      i.source_recipes?.some(r => searchLabel(r).toLowerCase().includes(term)) ||
      i.purchase_names?.some(name => name.toLowerCase().includes(term))

    const filterItems = (items) => {
      let result = items || []

      if (activeFilter === 'to_order') {
        result = result.filter(i => i.to_order > 0 && !i.is_on_hand)
      } else if (citySuppliers.some(s => (s.name || s) === activeFilter)) {
        result = result.filter(i => i.supplier_name === activeFilter)
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase()
        result = result.filter(i => matchesSearch(i, term))
      }

      return result
    }

    const filterSauces = (items) => {
      if (!searchTerm) return items || []
      const term = searchTerm.toLowerCase()
      return (items || []).filter(i => matchesSearch(i, term))
    }

    // Sort vegetables by meal name (first dish) alphabetically
    const sortByMeal = (items) => {
      return [...items].sort((a, b) => {
        const mealA = a.dishes?.[0]?.dish_name || ''
        const mealB = b.dishes?.[0]?.dish_name || ''
        return mealA.localeCompare(mealB)
      })
    }

    return {
      ...data,
      proteins: filterItems(data.proteins),
      vegetables: sortByMeal(filterItems(data.vegetables)),
      carbs: filterItems(data.carbs),
      sauces: filterSauces(data.sauces),
      bases: filterItems(data.bases),
      wellness_shots: data.wellness_shots || [],
      additional: {
        liquids: filterItems(data.additional?.liquids),
        dry_goods: filterItems(data.additional?.dry_goods),
        refrigerated: filterItems(data.additional?.refrigerated),
      }
    }
  }, [data, searchTerm, activeFilter])

  const itemsToOrderCount = useMemo(() => {
    if (!data) return 0
    let count = 0
    const categories = ['proteins', 'vegetables', 'carbs', 'bases']
    categories.forEach(cat => {
      count += (data[cat] || []).filter(i => i.to_order > 0 && !i.is_on_hand).length
    })
    if (data.additional) {
      count += (data.additional.liquids || []).filter(i => i.to_order > 0).length
      count += (data.additional.dry_goods || []).filter(i => i.to_order > 0).length
      count += (data.additional.refrigerated || []).filter(i => i.to_order > 0).length
    }
    return count
  }, [data])

  const hasNoIngredients = data &&
    (data.proteins?.length || 0) === 0 &&
    (data.vegetables?.length || 0) === 0 &&
    (data.carbs?.length || 0) === 0 &&
    (data.sauces?.length || 0) === 0

  function handlePrint() {
    exportAPI.openOrderSheet(currentMenu, isSpanish ? 'spanish' : null)
  }

  function handlePrepSheet() {
    exportAPI.openPrepSheet(currentMenu, isSpanish ? 'spanish' : null)
  }

  async function handleExportOrderSheetMD() {
    if (!data) return
    try {
      const menu = await menusAPI.get(currentMenu)
      const md = buildOrderSheetMD(data, menu?.name, currentCity)
      downloadMD(buildFilename('order-sheet', { city: currentCity, menu: menu?.name }), md)
      success('Order sheet exported for AI')
    } catch (err) {
      error('Failed to export order sheet: ' + (err.message || 'Unknown error'))
    }
  }

  async function handleExportPrepSheetMD() {
    if (!data) return
    try {
      const menu = await menusAPI.get(currentMenu)
      const md = buildPrepSheetMD(data, menu?.name, currentCity)
      downloadMD(buildFilename('prep-sheet', { city: currentCity, menu: menu?.name }), md)
      success('Prep sheet exported for AI')
    } catch (err) {
      error('Failed to export prep sheet: ' + (err.message || 'Unknown error'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-havn-green-700 animate-spin" />
      </div>
    )
  }

  if (!currentMenu) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-havn-gray-300 mb-4" />
        <h3 className="font-display text-lg font-semibold text-havn-gray-500">No Menu Selected</h3>
        <p className="text-sm text-havn-gray-400 mt-1">
          Select a menu from the sidebar to view ordering
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto animate-fade-in">
      {/* Summary Cards */}
      {data && <SummaryCards data={data} />}

      {/* Empty state — meal counts needed */}
      {hasNoIngredients && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-amber-500" />
          </div>
          <h3 className="font-display text-xl font-semibold text-havn-green-900 mb-2">
            Meal Counts Needed
          </h3>
          <p className="text-sm text-havn-gray-500 max-w-md mb-6">
            The ordering calculations need meal counts to work. Head to the Meal Counts page
            to enter how many of each dish to prepare, or import counts from Airtable.
          </p>
          <Link
            to="/meal-counts"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-havn-green-700 text-white rounded-lg font-medium hover:bg-havn-green-800 transition-colors"
          >
            <Utensils className="w-4 h-4" />
            Go to Meal Counts
          </Link>
        </div>
      )}

      {/* Filter Tabs */}
      {!hasNoIngredients && <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { id: 'all', label: 'All' },
          { id: 'to_order', label: 'To Order', count: itemsToOrderCount },
          ...citySuppliers.map(s => ({ id: s.name || s, label: s.name || s }))
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveFilter(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              activeFilter === tab.id
                ? 'bg-havn-green-700 text-white'
                : 'bg-havn-cream-200 text-havn-gray-600 hover:bg-havn-cream-300'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                activeFilter === tab.id ? 'bg-white/20' : 'bg-havn-green-100 text-havn-green-700'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>}

      {!hasNoIngredients && <>
      {/* Action Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-havn-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search ingredients, dishes, suppliers..."
              className="input pl-10"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-havn-gray-400 hover:text-havn-charcoal"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Expand/Collapse */}
          <button
            onClick={() => setAllExpanded(true)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              allExpanded ? 'bg-havn-green-100 text-havn-green-700' : 'bg-havn-cream-200 text-havn-gray-600 hover:bg-havn-cream-300'
            }`}
          >
            <ChevronDown className="w-4 h-4" />
            <span className="hidden md:inline">Expand</span>
          </button>
          <button
            onClick={() => setAllExpanded(false)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              !allExpanded ? 'bg-havn-green-100 text-havn-green-700' : 'bg-havn-cream-200 text-havn-gray-600 hover:bg-havn-cream-300'
            }`}
          >
            <ChevronRight className="w-4 h-4" />
            <span className="hidden md:inline">Collapse</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Undo/Redo */}
          <div className="flex items-center border-r border-havn-cream-300 pr-2 mr-1">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className={`p-2 rounded-lg transition-colors ${
                canUndo ? 'text-havn-gray-600 hover:bg-havn-cream-200 hover:text-havn-charcoal' : 'text-havn-gray-300 cursor-not-allowed'
              }`}
              title={canUndo ? `Undo: ${getUndoDescription()}` : 'Nothing to undo'}
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className={`p-2 rounded-lg transition-colors ${
                canRedo ? 'text-havn-gray-600 hover:bg-havn-cream-200 hover:text-havn-charcoal' : 'text-havn-gray-300 cursor-not-allowed'
              }`}
              title={canRedo ? `Redo: ${getRedoDescription()}` : 'Nothing to redo'}
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>

          <button onClick={() => setShowVersionModal(true)} className="btn-secondary">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">History</span>
          </button>
          <button onClick={handleOpenRecovery} className="btn-secondary text-amber-600 hover:text-amber-700">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Recover</span>
          </button>
          <button onClick={() => setShowColumnSettings(true)} className="btn-secondary">
            <Settings2 className="w-4 h-4" />
            <span className="hidden sm:inline">Columns</span>
          </button>
          <button onClick={() => setShowImportModal(true)} className="btn-secondary">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button onClick={loadData} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button onClick={handlePrepSheet} className="btn-secondary">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Prep Sheet</span>
          </button>
          <button
            onClick={handleExportPrepSheetMD}
            className="btn-secondary"
            title="Download Prep Sheet as markdown for feeding into an LLM"
          >
            <FileCode className="w-4 h-4" />
            <span className="hidden sm:inline">Prep .md</span>
          </button>
          <button onClick={handlePrint} className="btn-primary">
            <Printer className="w-4 h-4" />
            Order Sheet
          </button>
          <button
            onClick={handleExportOrderSheetMD}
            className="btn-secondary"
            title="Download Order Sheet as markdown for feeding into an LLM"
          >
            <FileCode className="w-4 h-4" />
            <span className="hidden sm:inline">Order .md</span>
          </button>
        </div>
      </div>

      {/* Hint */}
      <div className="text-xs text-havn-gray-400 mb-4">
        Click any "On Hand" or "Ordered" value to edit - 5% buffer included - Click <Plus className="w-3 h-3 inline" /> to add split orders
      </div>

      {/* Main Components Section */}
      {filteredData && (
        <>
          <div className="mb-8">
            <h2 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
              Main Components
            </h2>
            <OrderTable
              title="Proteins"
              categoryKey="proteins"
              items={filteredData.proteins || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={10}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
            <OrderTable
              title="Produce"
              categoryKey="vegetables"
              items={filteredData.vegetables || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={10}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
            <OrderTable
              title="Carbs & Grains"
              categoryKey="carbs"
              items={filteredData.carbs || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={10}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
          </div>

          {/* Sauces & prepared sides */}
          <div className="mb-8">
            <h2 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
              Sauces & prepared sides
              <span className="text-sm font-normal text-havn-gray-400 ml-2">(Sauces are made in-house)</span>
            </h2>
            <SauceTable
              title="Sauces"
              items={filteredData.sauces || []}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewSourceRecipe}
              menuId={currentMenu}
              cityId={currentCity}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
            />
            <OrderTable
              title="Prepared sides & toppings"
              categoryKey="bases"
              items={filteredData.bases || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={10}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
          </div>

          {/* Wellness Shots */}
          <WellnessShotsSection
            items={filteredData.wellness_shots}
            summary={data?.summary}
          />

          {/* Pantry & refrigerated */}
          <div>
            <h2 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
              Pantry & refrigerated
              <span className="text-sm font-normal text-havn-gray-400 ml-2">(Includes toppings and recipe ingredients)</span>
            </h2>
            <OrderTable
              title="Liquids"
              categoryKey="liquids"
              items={filteredData.additional?.liquids || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={1}
              isAdditional={true}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
            <OrderTable
              title="Dry Goods"
              categoryKey="dry_goods"
              items={filteredData.additional?.dry_goods || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={1}
              isAdditional={true}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
            <OrderTable
              title="Refrigerated"
              categoryKey="refrigerated"
              items={filteredData.additional?.refrigerated || []}
              columns={columns}
              cityId={currentCity}
              menuId={currentMenu}
              onRefresh={loadData}
              onOpenDetail={setSelectedIngredient}
              splitOrders={splitOrders}
              onSplitOrdersChange={handleSplitOrdersChange}
              forceExpanded={allExpanded}
              onViewRecipe={handleViewRecipe}
              onViewSourceRecipe={handleViewSourceRecipe}
              onViewDish={handleViewDish}
              suppliers={citySuppliers}
              orderedStep={1}
              isAdditional={true}
              onDataChange={handleDataChange}
              onItemPatch={patchItem}
              onFactorChange={handleFactorChange}
            />
          </div>

          {/* Inventory (imported from CSV) */}
          {inventoryItems.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-lg font-semibold text-havn-green-900 mb-4">
                Inventory
                <span className="text-sm font-normal text-havn-gray-400 ml-2">(Imported from CSV)</span>
              </h2>
              <InventoryTable
                items={inventoryItems}
                menuId={currentMenu}
                onRefresh={loadData}
                onDataChange={handleDataChange}
                forceExpanded={allExpanded}
              />
            </div>
          )}
        </>
      )}
      </>}

      {/* Modals */}
      {showColumnSettings && (
        <ColumnSettingsModal
          columns={columns}
          onChange={setColumns}
          onClose={() => setShowColumnSettings(false)}
        />
      )}

      {showImportModal && (
        <ImportModal
          menuId={currentMenu}
          cityId={currentCity}
          onClose={() => setShowImportModal(false)}
          onSuccess={loadData}
        />
      )}

      {selectedIngredient && (
        <IngredientDetailModal
          ingredient={selectedIngredient}
          cityId={currentCity}
          menuId={currentMenu?.id}
          onClose={() => setSelectedIngredient(null)}
          onUpdate={loadData}
          onViewRecipe={handleViewSourceRecipe}
        />
      )}

      {viewingRecipe && (
        <ScaledRecipeModal
          recipeId={viewingRecipe.recipeId}
          dishId={viewingRecipe.dishId}
          recipeName={viewingRecipe.recipeName}
          targetQuantity={viewingRecipe.targetQuantity}
          targetCount={viewingRecipe.targetCount}
          isDish={viewingRecipe.isDish}
          onClose={() => setViewingRecipe(null)}
        />
      )}

      {showVersionModal && (
        <VersionHistoryModal
          versions={versions}
          onRevert={handleRevertToVersion}
          onClose={() => setShowVersionModal(false)}
        />
      )}

      {showRecoveryModal && (
        <RecoveryModal
          snapshots={snapshots}
          loading={loadingSnapshots}
          onRestore={handleRestoreSnapshot}
          onClose={() => setShowRecoveryModal(false)}
          onRefresh={loadSnapshots}
        />
      )}
    </div>
  )
}
